import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Factory, UserPlus, X, Edit, Phone, User, Play, Pause, 
  ArrowLeft, ArrowRightLeft, ChevronRight, 
  UserCircle, MapPin, Building2, Wallet, 
  ClipboardList, Mail, LogOut, MessageCircle, PhoneCall,
  Clock, ChevronRight as ChevronRightIcon,
  Edit3, IndianRupee, LayoutDashboard, Users,
  Activity, Layers, Calendar, CalendarDays, Search, Filter, Sun, Moon,
  UserX, Lock, Smartphone, Settings, Bell, Image as ImageIcon,
  Volume2, VolumeX, HelpCircle, PlayCircle, Send, Download
} from 'lucide-react';

import { createPortal } from 'react-dom';

import './App.css';

// --- IMPORTING PAGES ---
import PaymentView from './components/owner/pages/PaymentView';
import Inventory from './components/owner/pages/Inventory';
import Workers from './components/owner/pages/Workers';
import BadliHistory from './components/owner/pages/BadliHistory'; 
import Vacancies from './components/owner/pages/Vacancies';
import WorkerApp from './components/worker/WorkerApp';
import { useSmartGuide } from './components/owner/pages/useSmartGuide'; 

// 👇 यह नई फाइल है जिसे हमने अभी अलग किया है
import FloatingVoiceAI from './FloatingVoiceAI'; 
import ProfileSettings from './components/owner/pages/ProfileSettings';
import Modal from './components/ui/Modal';
import Header from './components/ui/Header';
import BottomNav from './components/ui/BottomNav';
import ProductionReportModal from './components/ui/PdfExportModal';

const API_BASE = 'https://mukadampro.shirpur.store';

// 🌟 NAYA: PARSE TIME & FORMAT TIME HELPERS (Duty duration nikalne ke liye zaroori)
const parseTimeStr = (t) => { 
    if (!t) return 0;
    let timeStr = String(t).trim();
    timeStr = timeStr.replace(/^\d{4}-\d{2}-\d{2}\s+/, '');
    
    let isPM = timeStr.toUpperCase().includes('PM');
    let isAM = timeStr.toUpperCase().includes('AM');
    
    let cleanTime = timeStr.replace(/PM|AM/i, '').trim();
    let [h, m] = cleanTime.split(':').map(Number);
    
    h = h || 0;
    m = m || 0;
    
    if (isPM && h !== 12) h += 12;
    if (isAM && h === 12) h = 0;
    
    return h * 60 + m; 
};

const formatTimeForDisplay = (t) => { 
    if (!t) return ""; 
    let timeStr = String(t).trim().replace(/^\d{4}-\d{2}-\d{2}\s+/, ''); 
    if (timeStr.toUpperCase().includes('PM') || timeStr.toUpperCase().includes('AM')) return timeStr; 
    let [h, m] = timeStr.split(':'); 
    h = parseInt(h, 10) || 0; 
    const ampm = h >= 12 ? 'PM' : 'AM'; 
    return `${h % 12 || 12}:${m || '00'} ${ampm}`; 
};

// Default Owner Info
const OWNER_CREDENTIALS = { id: 'O1', name: 'Owner Sir', mobile: '0000', pin: '0000', role: 'owner' };

export const GlobalThemeEngine = () => {
    const [theme, setTheme] = useState({
        font: localStorage.getItem('owner_appFont') || 'Plus Jakarta Sans',
        scale: parseFloat(localStorage.getItem('owner_appScale')) || 1.0,
        boxStyle: localStorage.getItem('owner_appBoxStyle') || 'glass',
        boxTexture: localStorage.getItem('owner_appBoxTexture') || 'none', 
        neonGlow: parseInt(localStorage.getItem('owner_appNeonGlow')) || 0,
        animSpeed: localStorage.getItem('owner_appAnimSpeed') || 'smooth',
        pageAnim: localStorage.getItem('owner_pageAnimStyle') || 'zoom',
        textSize: parseInt(localStorage.getItem('owner_appTextSize')) || 12,
        textColor: localStorage.getItem('owner_appTextColor') || '#111111',
        textGlow: parseInt(localStorage.getItem('owner_appTextGlow')) || 0,
        auroraBg: localStorage.getItem('owner_appAuroraBg') !== 'false',
        rippleEffect: localStorage.getItem('owner_appRippleEffect') !== 'false',
    });

    useEffect(() => {
        const handleThemeUpdate = () => {
            setTheme(prevTheme => ({
                ...prevTheme,
                font: localStorage.getItem('owner_appFont') || 'Plus Jakarta Sans',
                scale: parseFloat(localStorage.getItem('owner_appScale')) || 1.0,
                boxStyle: localStorage.getItem('owner_appBoxStyle') || 'glass',
                boxTexture: localStorage.getItem('owner_appBoxTexture') || 'none',
                neonGlow: parseInt(localStorage.getItem('owner_appNeonGlow')) || 0,
                animSpeed: localStorage.getItem('owner_appAnimSpeed') || 'smooth',
                pageAnim: localStorage.getItem('owner_pageAnimStyle') || 'zoom',
                textSize: parseInt(localStorage.getItem('owner_appTextSize')) || 12,
                textColor: localStorage.getItem('owner_appTextColor') || '#111111',
                textGlow: parseInt(localStorage.getItem('owner_appTextGlow')) || 0,
                auroraBg: localStorage.getItem('owner_appAuroraBg') !== 'false',
                rippleEffect: localStorage.getItem('owner_appRippleEffect') !== 'false',
            }));
            
            document.documentElement.style.zoom = localStorage.getItem('owner_appScale') || 1.0;
            document.documentElement.style.fontFamily = localStorage.getItem('owner_appFont') || 'Plus Jakarta Sans';
        };
        
        // Listen ONLY to Owner's event
        window.addEventListener('ownerThemeChanged', handleThemeUpdate);
        window.addEventListener('storage', handleThemeUpdate);
        
        return () => {
            window.removeEventListener('ownerThemeChanged', handleThemeUpdate);
            window.removeEventListener('storage', handleThemeUpdate);
        };
    }, []);

    // 🚀 100x REAL LIQUID RIPPLE EFFECT
    useEffect(() => {
        if (!theme.rippleEffect) return;
        const handleRipple = (e) => {
            const target = e.target.closest('button, .ui-card, .ui-setting-item, div[style*="cursor: pointer"]');
            if (!target) return;
            
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            const rect = target.getBoundingClientRect();
            
            const ripple = document.createElement('span');
            ripple.className = 'premium-liquid-ripple';
            ripple.style.left = `${clientX - rect.left}px`;
            ripple.style.top = `${clientY - rect.top}px`;
            
            if (window.getComputedStyle(target).position === 'static') { target.style.position = 'relative'; }
            target.style.overflow = 'hidden';
            target.appendChild(ripple);
            
            setTimeout(() => ripple.remove(), 1000); 
        };
        window.addEventListener('mousedown', handleRipple);
        window.addEventListener('touchstart', handleRipple, { passive: true });
        return () => { window.removeEventListener('mousedown', handleRipple); window.removeEventListener('touchstart', handleRipple); }
    }, [theme.rippleEffect]);

    const animDuration = theme.animSpeed === 'fast' ? '0.15s' : theme.animSpeed === 'relaxed' ? '0.6s' : '0.3s';
    const textMultiplier = theme.textSize / 12; 
    const usedFontSizes = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 18, 20, 24, 28, 32, 40];
    const dynamicTextStyles = usedFontSizes.map(size => {
        const perfectFontSize = ((size * textMultiplier) / theme.scale).toFixed(2);
        return `*[style*="font-size: ${size}px"], *[style*="font-size:${size}px"] { font-size: ${perfectFontSize}px !important; }`;
    }).join('\n');

    let boxCss = '';
    const intFactor = 0.5; // Fixed value for default transparency (50%)
    const neonFactor = theme.neonGlow / 100;
    const neonShadow = neonFactor > 0 ? `0 0 ${theme.neonGlow}px rgba(16, 185, 129, ${neonFactor}), inset 0 0 ${theme.neonGlow/2}px rgba(16, 185, 129, ${neonFactor * 0.5})` : 'none';
    const neonBorder = neonFactor > 0 ? `1px solid rgba(16, 185, 129, ${0.3 + neonFactor})` : `1px solid rgba(255, 255, 255, ${0.2 + (intFactor * 0.5)})`;

    // 🌟 100x PHYSICAL TEXTURES ENGINE (No Images, Pure CSS)
    let textureCSS = '';
    if (theme.boxTexture === 'grain') {
        // SVG Base64 Noise (Perfect Matte Film Grain)
        textureCSS = `background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.15'/%3E%3C/svg%3E");`;
    } else if (theme.boxTexture === 'mesh') {
        // Micro Dot Mesh
        textureCSS = `background-image: radial-gradient(rgba(0,0,0,0.1) 1px, transparent 1px); background-size: 6px 6px;`;
    } else if (theme.boxTexture === 'brushed') {
        // Brushed Metal
        textureCSS = `background-image: repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 4px), repeating-linear-gradient(-45deg, rgba(0,0,0,0.02) 0px, rgba(0,0,0,0.02) 1px, transparent 1px, transparent 4px);`;
    } else if (theme.boxTexture === 'carbon') {
        // Premium Subtle Carbon Fiber
        textureCSS = `background-image: repeating-linear-gradient(45deg, rgba(0,0,0,0.05) 25%, transparent 25%, transparent 75%, rgba(0,0,0,0.05) 75%, rgba(0,0,0,0.05)), repeating-linear-gradient(45deg, rgba(0,0,0,0.05) 25%, transparent 25%, transparent 75%, rgba(0,0,0,0.05) 75%, rgba(0,0,0,0.05)); background-position: 0 0, 8px 8px; background-size: 16px 16px;`;
    }

    // 🌟 100x MASTER SELECTOR: Targets ALL cards in the entire App (Dashboard, Looms, Settings)
    const targetCards = `:is(.ui-card, .glass-panel, div[style*="background: rgba(255, 255, 255, 0.25)"], div[style*="background: rgba(255,255,255,0.25)"], div[style*="background: rgba(255,255,255,0.3)"])`;

    // 🌟 WORLD-CLASS 100X BOX ARCHITECTURES & TOUCH PHYSICS
    const dist = 6 + (intFactor * 10);
    const blur = 12 + (intFactor * 20);

    if (theme.boxStyle === 'glass') {
        const blurAmount = 15 + (intFactor * 35);
        const bgOp = 0.05 + (intFactor * 0.3);
        boxCss = `
            ${targetCards} {
                background: linear-gradient(135deg, rgba(255,255,255, ${bgOp + 0.15}) 0%, rgba(255,255,255, ${bgOp}) 100%) !important;
                backdrop-filter: blur(${blurAmount}px) saturate(120%) !important;
                -webkit-backdrop-filter: blur(${blurAmount}px) saturate(120%) !important;
                border: 1px solid rgba(255,255,255,0.15) !important;
                border-top: 1px solid rgba(255,255,255,0.4) !important; /* Sub-pixel top highlight */
                border-left: 1px solid rgba(255,255,255,0.3) !important; /* Sub-pixel left highlight */
                box-shadow: 
                    0 10px 40px -10px rgba(31, 38, 135, ${intFactor * 0.3}), /* Deep soft shadow */
                    inset 0 1px 3px rgba(255,255,255,0.6), /* Inner glass reflection */
                    inset 0 -1px 3px rgba(0,0,0,0.05),
                    ${neonShadow} !important;
                position: relative;
                /* 👇 GPU composition locking aur iOS dynamic timing adjustment */
                backface-visibility: hidden;
                transform-style: preserve-3d;
                will-change: transform, box-shadow;
                transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
            }
            /* 👇 Snappy iOS Elastic Click shrink effect code */
            ${targetCards}:active { 
                transform: scale(0.95) translateZ(0) !important; 
                box-shadow: 0 4px 10px rgba(0,0,0,0.05) !important; 
                transition: transform 0.08s cubic-bezier(0.25, 1, 0.5, 1) !important;
            }
            ${targetCards}::before {
                content: ''; position: absolute; top: 0; left: -150%; width: 50%; height: 100%;
                background: linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0) 100%);
                transform: skewX(-25deg); animation: glass-shine 8s infinite cubic-bezier(0.4, 0, 0.2, 1); pointer-events: none; z-index: 1;
            }
            ${targetCards}::after {
                content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 100%;
                ${textureCSS} mix-blend-mode: overlay; pointer-events: none; z-index: 0; border-radius: inherit; opacity: 0.7;
            }
        `;
        
    } else if (theme.boxStyle === 'acrylic') {
        const blurAmount = 30 + (intFactor * 50);
        const bgOp = 0.2 + (intFactor * 0.4);
        boxCss = `
            ${targetCards} {
                background: radial-gradient(circle at top left, rgba(255,255,255, ${bgOp + 0.2}), rgba(255,255,255, ${bgOp - 0.1})) !important;
                backdrop-filter: blur(${blurAmount}px) saturate(180%) brightness(110%) !important;
                -webkit-backdrop-filter: blur(${blurAmount}px) saturate(180%) brightness(110%) !important;
                border: 1px solid rgba(255,255,255,0.2) !important;
                border-top: 1px solid rgba(255,255,255,0.5) !important;
                box-shadow: 
                    0 15px 35px 0 rgba(0, 0, 0, ${intFactor * 0.25}), 
                    inset 0 0 30px rgba(255,255,255,0.6), /* Internal light scattering */
                    ${neonShadow} !important;
                position: relative;
                transition: transform 0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
            }
            ${targetCards}:active { transform: scale(0.97) !important; }
            ${targetCards}::after {
                content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 100%;
                ${textureCSS} mix-blend-mode: overlay; pointer-events: none; z-index: 0; border-radius: inherit; opacity: 0.8;
            }
        `;
    } else if (theme.boxStyle === 'solid') {
        const shadowOp = intFactor * 0.12;
        boxCss = `
            ${targetCards} {
                background: linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%) !important;
                border: 1px solid rgba(226, 232, 240, 0.8) !important;
                box-shadow: 
                    0 4px 6px -1px rgba(0, 0, 0, ${shadowOp}),
                    0 12px 20px -3px rgba(0, 0, 0, ${shadowOp * 1.5}), /* Diffused premium drop shadow */
                    inset 0 2px 4px rgba(255,255,255,1),
                    ${neonShadow} !important;
                position: relative;
                transition: transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.2s ease !important;
            }
            ${targetCards}:active { transform: scale(0.98) !important; box-shadow: 0 2px 5px rgba(0,0,0,0.1) !important; }
            ${targetCards}::after {
                content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 100%;
                ${textureCSS} mix-blend-mode: multiply; pointer-events: none; z-index: 0; border-radius: inherit; opacity: 0.3;
            }
        `;
    } else if (theme.boxStyle === 'neumorph') {
        boxCss = `
            ${targetCards} {
                background: linear-gradient(135deg, #f3f5f8, #e0e5ec) !important;
                border: 1px solid rgba(255,255,255,0.4) !important;
                box-shadow: 
                    ${dist}px ${dist}px ${blur}px rgba(163,177,198, ${0.4 + intFactor*0.3}), 
                    -${dist}px -${dist}px ${blur}px rgba(255,255,255, ${0.6 + intFactor*0.4}),
                    inset 1px 1px 2px rgba(255,255,255,0.5),
                    ${neonShadow} !important;
                position: relative;
                transition: transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.2s ease !important;
            }
            /* 100x Real Physical Button press effect */
            ${targetCards}:active { 
                transform: scale(0.98) !important; 
                box-shadow: 
                    inset ${dist/2}px ${dist/2}px ${blur/2}px rgba(163,177,198,0.5), 
                    inset -${dist/2}px -${dist/2}px ${blur/2}px rgba(255,255,255,0.8) !important; 
            }
            ${targetCards}::after {
                content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 100%;
                ${textureCSS} mix-blend-mode: multiply; pointer-events: none; z-index: 0; border-radius: inherit; opacity: 0.2;
            }
        `;
    }

    const textGlowFactor = theme.textGlow / 100;
    const dynamicTextShadow = theme.textGlow > 0 
        ? `text-shadow: 0 0 ${2 + (textGlowFactor * 4)}px ${theme.textColor}90, 0 0 ${4 + (textGlowFactor * 10)}px ${theme.textColor}60, 0 0 ${10 + (textGlowFactor * 20)}px ${theme.textColor}30 !important;` 
        : '';

    return (
        <>
        <style>{`
            /* 👇 10 Premium Fonts Included 👇 */
            @import url('https://fonts.googleapis.com/css2?family=Fira+Sans:wght@400;600;800;900&family=Inter:wght@400;600;800;900&family=Montserrat:wght@400;600;800;900&family=Nunito:wght@400;600;800;900&family=Outfit:wght@400;600;800;900&family=Playfair+Display:wght@400;600;800;900&family=Poppins:wght@400;600;800;900&family=Plus+Jakarta+Sans:wght@400;600;800;900&family=Quicksand:wght@400;600;700&family=Raleway:wght@400;600;800;900&display=swap');
            
            * { font-family: '${theme.font}', sans-serif !important; text-transform: uppercase !important; }
           html { 
    zoom: 0.95 !important; 
    font-size: 14px !important; 
}
body, * {
    font-size: 14px !important;
}
            ${dynamicTextStyles}
            
            h1, h2, h3, h4, h5, p, span { color: ${theme.textColor}; ${dynamicTextShadow} }
            
            ${boxCss}

  @keyframes glass-shine { 0% { left: -150%; } 15% { left: 250%; } 100% { left: 250%; } }
            @keyframes page-zoom { 0% { opacity: 0; transform: scale(0.95) translateZ(0); } 100% { opacity: 1; transform: scale(1) translateZ(0);} }
            @keyframes page-slide { 0% { opacity: 0; transform: translateY(30px) translateZ(0); } 100% { opacity: 1; transform: translateY(0) translateZ(0); } }
            @keyframes page-fade { 0% { opacity: 0; } 100% { opacity: 1; } }
            @keyframes page-flip { 0% { opacity: 0; transform: perspective(1000px) rotateX(-10deg) translateZ(0); } 100% { opacity: 1; transform: perspective(1000px) rotateX(0deg) translateZ(0); } }
            
            .page-animate { 
                animation: page-${theme.pageAnim} ${animDuration} cubic-bezier(0.16, 1, 0.3, 1) forwards !important; 
                will-change: transform, opacity; 
            }

            /* 🌟 100x REAL LIQUID RIPPLE PHYSICS */
            .premium-liquid-ripple {
                position: absolute; border-radius: 50%; transform: scale(0);
                animation: ripple-wave 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
                background: radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.4) 40%, rgba(255,255,255,0) 80%);
                box-shadow: 0 0 20px rgba(255,255,255,0.6), inset 0 0 20px rgba(255,255,255,0.4);
                pointer-events: none; width: 150px; height: 150px;
                margin-top: -75px; margin-left: -75px; z-index: 10;
            }
            @keyframes ripple-wave { 
                0% { transform: scale(0.1); opacity: 1; } 
                70% { transform: scale(3.5); opacity: 0.3; } 
                100% { transform: scale(4.5); opacity: 0; } 
            }

            /* 🌟 100x DYNAMIC AURORA BLEND (MULTI-LAYER) */
            .aurora-bg-container {
                position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
                z-index: 0; pointer-events: none; overflow: hidden;
                mix-blend-mode: hard-light; opacity: 0.85;
            }
            .aurora-layer {
                position: absolute; border-radius: 50%; filter: blur(100px);
                animation: aurora-float 25s infinite cubic-bezier(0.4, 0, 0.2, 1) alternate;
                will-change: transform;
            }
            .aurora-1 { width: 90vw; height: 90vh; background: rgba(37, 99, 235, 0.4); top: -20%; left: -20%; }
            .aurora-2 { width: 80vw; height: 80vh; background: rgba(16, 185, 129, 0.4); bottom: -20%; right: -10%; animation-delay: -7s; }
            .aurora-3 { width: 100vw; height: 100vh; background: rgba(139, 92, 246, 0.4); top: 10%; left: 20%; animation-delay: -14s; mix-blend-mode: screen; }

            @keyframes aurora-float {
                0% { transform: translate(0, 0) scale(1) rotate(0deg); }
                33% { transform: translate(10vw, -10vh) scale(1.1) rotate(45deg); }
                66% { transform: translate(-10vw, 5vh) scale(0.9) rotate(-45deg); }
                100% { transform: translate(5vw, 15vh) scale(1.2) rotate(15deg); }
            }

            .skeleton-box {
                background: linear-gradient(90deg, rgba(255,255,255,0.1) 25%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0.1) 75%);
                background-size: 400% 100%; animation: skeleton-loading 1.5s infinite ease-in-out;
                border-radius: 16px; border: 1px solid rgba(255,255,255,0.2);
            }
            @keyframes skeleton-loading { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        `}</style>
        
        {/* 🚀 100x MULTI-LAYER AURORA RENDERER */}
        {theme.auroraBg && (
            <div className="aurora-bg-container">
                <div className="aurora-layer aurora-1" />
                <div className="aurora-layer aurora-2" />
                <div className="aurora-layer aurora-3" />
            </div>
        )}
        </>
    );
};

// --- HELPERS ---
const isLoomActive = (lastUpdated) => {
  if (!lastUpdated) return false;
  const updated = new Date(lastUpdated.replace(' ', 'T')); 
  const now = new Date();
  const diffMinutes = (now - updated) / 1000 / 60;
  return !isNaN(diffMinutes) && diffMinutes < 10; 
};

// 👇 इसे यहाँ ऐड किया गया है ताकि पूरा ऐप इसे इस्तेमाल कर सके
const triggerHaptic = (pattern = [30]) => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) { navigator.vibrate(pattern); }
};

// --- COMPONENT: LOGIN VIEW ---
const LoginView = ({ onLogin, appBg, handleVoiceClick, handleFocusVoice, handleTextBlurVoice }) => {
    const [role, setRole] = useState('owner');
    const [mobile, setMobile] = useState('');
    const [pin, setPin] = useState('');
    const [loading, setLoading] = useState(false);
    
    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        handleVoiceClick(e, "लॉग इन किया जा रहा है, कृपया प्रतीक्षा करें।", null);
        if (role === 'owner') {
            setTimeout(() => {
                if (mobile === OWNER_CREDENTIALS.mobile && pin === OWNER_CREDENTIALS.pin) {
                    onLogin(OWNER_CREDENTIALS);
                } else {
                    alert("Invalid Owner credentials! (Use: 0000 / 0000)");
                }
                setLoading(false);
            }, 800);
        } else {
            try {
                const url = `${API_BASE}/login_worker.php`;
                const res = await fetch(url, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ mobile: mobile, password: pin })
                });
                const text = await res.text();
                try {
                    const json = JSON.parse(text);
                    if (json.status === 'success') { onLogin({ ...json.data, role: 'worker' }); } 
                    else { alert(json.message || "Galat Mobile Number ya Password!"); }
                } catch (parseError) { alert("Server error! Kripya console check karein."); }
            } catch (err) { alert("Network Error! Server se connect nahi ho paya."); } 
            finally { setLoading(false); }
        }
    };

    // 🌟 Scaled down sizes matching Workers.js 🌟
    const tabStyle = { flex: 1, border: 'none', padding: '8px 12px', borderRadius: '12px', fontSize: '10px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' };

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px', background: appBg, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            
            {/* 🌟 PERFECT SIZED SOLID WHITE LOGIN CARD 🌟 */}
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', padding: '24px 20px', width: '100%', maxWidth: '360px', boxShadow: '0 10px 30px -10px rgba(0, 0, 0, 0.08)', boxSizing: 'border-box', position: 'relative', zIndex: 10 }}>
                
                {/* 🌟 HEADER SECTION 🌟 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ background: 'linear-gradient(135deg, #E0F2FE, #7DD3FC)', padding: '8px', borderRadius: '12px', boxShadow: '0 4px 10px rgba(2, 132, 199, 0.15)' }}>
                            <Lock size={16} color="#0284C7"/>
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#0F172A', letterSpacing: '-0.5px' }}>Welcome</h2>
                            <span style={{ fontSize: '8px', color: '#64748B', fontWeight: '600', letterSpacing: '0.8px', textTransform: 'uppercase' }}>Log in to continue</span>
                        </div>
                    </div>
                </div>

                {/* 🌟 ROLE TOGGLE 🌟 */}
                <div style={{ display: 'flex', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '4px', gap: '4px', marginBottom: '20px' }}>
                    <button type="button" onClick={(e) => handleVoiceClick(e, "ओनर का अकाउंट सेलेक्ट किया गया है।", () => setRole('owner'))} style={{...tabStyle, background: role==='owner'?'#ffffff':'transparent', color: role==='owner'?'#0F172A':'#64748B', boxShadow: role==='owner'?'0 4px 10px rgba(0,0,0,0.05)':'none'}}>
                        <UserCircle size={14} color={role==='owner'?'#2563EB':'#64748B'} strokeWidth={2.5} /> Owner
                    </button>
                    <button type="button" onClick={(e) => handleVoiceClick(e, "वर्कर का अकाउंट सेलेक्ट किया गया है।", () => setRole('worker'))} style={{...tabStyle, background: role==='worker'?'#ffffff':'transparent', color: role==='worker'?'#0F172A':'#64748B', boxShadow: role==='worker'?'0 4px 10px rgba(0,0,0,0.05)':'none'}}>
                        <Users size={14} color={role==='worker'?'#059669':'#64748B'} strokeWidth={2.5} /> Worker
                    </button>
                </div>

                <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    
                    {/* 🌟 MOBILE INPUT 🌟 */}
                    <div style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', borderRadius: '10px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(79, 70, 229, 0.15)' }}>
                            <Smartphone size={14} color="#4338CA"/>
                        </div>
                        <input 
                            type="tel" placeholder="Mobile Number" required 
                            value={mobile} onChange={e=>setMobile(e.target.value)} 
                            onFocus={() => handleFocusVoice("अपना रजिस्टर्ड मोबाइल नंबर दर्ज करें।")}
                            onBlur={(e) => handleTextBlurVoice(e.target.value, "मोबाइल नंबर")}
                            style={{ width: '100%', padding: '14px 14px 14px 50px', borderRadius: '16px', border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: '11px', outline: 'none', boxSizing: 'border-box', color: '#0F172A', fontWeight: '600', transition: 'all 0.3s ease', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.01)' }} 
                        />
                    </div>

                    {/* 🌟 PASSWORD INPUT 🌟 */}
                    <div style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', background: 'linear-gradient(135deg, #FEF3C7, #FCD34D)', borderRadius: '10px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(217, 119, 6, 0.15)' }}>
                            <Lock size={14} color="#B45309"/>
                        </div>
                        <input 
                            type="password" placeholder="Password / PIN" required 
                            value={pin} onChange={e=>setPin(e.target.value)} 
                            onFocus={() => handleFocusVoice("यहाँ अपना पिन या पासवर्ड दर्ज करें।")}
                            style={{ width: '100%', padding: '14px 74px 14px 50px', borderRadius: '16px', border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: '11px', outline: 'none', boxSizing: 'border-box', color: '#0F172A', fontWeight: '600', transition: 'all 0.3s ease', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.01)' }} 
                        />
                        <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: '#ffffff', padding: '5px 10px', borderRadius: '8px', fontSize: '8px', fontWeight: '700', color: '#2563EB', cursor: 'pointer', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                            Forgot?
                        </div>
                    </div>

                    {/* 🌟 PREMIUM SUBMIT BUTTON 🌟 */}
                    <div style={{ marginTop: '4px' }}>
                        <button type="submit" disabled={loading} style={{ width: '100%', padding: '14px', borderRadius: '16px', background: '#0F172A', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', boxShadow: '0 8px 20px -5px rgba(15, 23, 42, 0.4)', transition: 'all 0.3s ease', fontSize: '12px', fontWeight: '700' }}>
                            {loading ? 'Authenticating...' : (
                                <>Secure Login <ChevronRight size={16} color="white" strokeWidth={3}/></>
                            )}
                        </button>
                    </div>
                </form>

                <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '9px', color: '#64748B', fontWeight: '600' }}>
                    {role === 'worker' ? "Apna registered Mobile Number aur Password daalen." : "Please login to securely access your dashboard."}
                </div>
            </div>

            {/* 🌟 PREMIUM BRANDING FOOTER 🌟 */}
            <div style={{ background: 'linear-gradient(135deg, #0F172A, #1E293B)', borderRadius: '20px', padding: '16px 20px', width: '100%', maxWidth: '360px', marginTop: '12px', color: 'white', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 10px 25px rgba(0,0,0,0.15)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: 'linear-gradient(135deg, #3B82F6, #2563EB)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(37, 99, 235, 0.4)' }}>
                    <Building2 size={18} color="#ffffff" />
                </div>
                <div>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', letterSpacing: '-0.3px', color: '#ffffff' }}>MukadamPro</h3>
                    <p style={{ margin: '2px 0 0 0', fontSize: '8px', color: '#94A3B8', fontWeight: '600', letterSpacing: '0.8px', textTransform: 'uppercase' }}>Textile Management System</p>
                </div>
            </div>
        </div>
    );
};



// --- COMPONENT: FULL SCREEN SEARCH MODAL (Owner) ---
const SearchModal = ({ onClose, workers, looms, appBg, handleVoiceClick, handleFocusVoice, handleSelectVoice, handleTextBlurVoice }) => {
    const [searchType, setSearchType] = useState('date'); 
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedId, setSelectedId] = useState('');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSearch = async () => {
        if ((searchType === 'worker' || searchType === 'loom') && !selectedId) {
            alert(`Please select a ${searchType === 'worker' ? 'Worker' : 'Loom'} to search.`);
            return;
        }
        setLoading(true); setResults(null);
        try {
            const res = await fetch(`${API_BASE}/search_api.php`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: searchType, start_date: startDate, end_date: endDate, id: selectedId })
            });
            const json = await res.json();
            setResults(json.status === 'success' ? json.data : []);
        } catch(e) { alert("Network Error"); } 
        finally { setLoading(false); }
    };

    const groupedData = useMemo(() => {
        if (!results) return null;
        if (results.length === 0) return [];
        const groups = [];
        results.forEach(r => {
            const shiftKey = r.shift || 'Day';
            const dateStr = r.date;
            const sTime = r.start_time || 'NA';
            const eTime = r.end_time || 'NA';
            const workerName = r.worker_name || 'Unknown Worker';
            const loomId = r.loom_id || 'NA';
            
            let foundGroup = groups.find(g => {
                if (g.date !== dateStr || g.shift !== shiftKey) return false;
                if (searchType === 'worker') {
                    if (g.items.some(item => item.loom_id === loomId)) return false;
                    if (g.sTime !== 'NA' && sTime !== 'NA' && g.sTime !== sTime) return false;
                    return true;
                } else if (searchType === 'loom') {
                    if (g.items.some(item => item.worker_name === workerName)) return false;
                    if (g.sTime !== 'NA' && sTime !== 'NA' && g.sTime !== sTime) return false;
                    return true;
                } else { return true; }
            });
            
            if (!foundGroup) {
                foundGroup = { date: dateStr, shift: shiftKey, sTime: sTime, eTime: eTime, workerName: workerName, total: 0, items: [], isOldDateApi: !r.loom_id && !r.worker_name && r.day !== undefined };
                groups.push(foundGroup);
            }
            foundGroup.items.push(r);
            if (r.meters !== undefined) foundGroup.total += parseFloat(r.meters || 0);
            else if (r.total !== undefined && foundGroup.isOldDateApi) foundGroup.total = parseFloat(r.total || 0); 
        });
        
        groups.forEach(g => {
            if (!g.isOldDateApi) {
                g.items.sort((a, b) => {
                    if (searchType === 'loom') return (a.worker_name || '').localeCompare(b.worker_name || '');
                    else return (parseInt(String(a.loom_id).replace(/[^0-9]/g, '')) || 0) - (parseInt(String(b.loom_id).replace(/[^0-9]/g, '')) || 0);
                });
            }
        });
        return groups.sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [results, searchType]);

   return (
        <Modal 
            isOpen={true} 
            onClose={(e) => {
                if(window.speechSynthesis) window.speechSynthesis.cancel();
                onClose(e);
            }} 
            title="Search Record" 
            icon={Search} 
            iconColor="#475569" 
            iconBg="#f8fafc" 
            maxWidth="480px"
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '16px' }}>
                    <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
                        <div style={styles.row}>
                            <div>
                                <label style={styles.fieldLabel}>From</label>
                                <input type="date" style={styles.input} value={startDate} onChange={e=>setStartDate(e.target.value)} onFocus={() => handleFocusVoice(" किस तारीख का प्रोडक्शन रिकॉर्ड देखना है, वह तारीख चुनें।")} />
                            </div>
                            <div>
                                <label style={styles.fieldLabel}>To</label>
                                <input type="date" style={styles.input} value={endDate} onChange={e=>setEndDate(e.target.value)} onFocus={() => handleFocusVoice(" किस तारीख तक का प्रोडक्शन रिकॉर्ड देखना है, वह तारीख चुनें।")} />
                            </div>
                        </div>
                        {searchType === 'worker' && (
                            <div>
                                <label style={styles.fieldLabel}>Worker</label>
                                <select style={styles.input} value={selectedId} onChange={e=>{ setSelectedId(e.target.value); handleSelectVoice(e.target.options[e.target.selectedIndex].text, " वर्कर"); }} onFocus={() => handleFocusVoice("सर्च करने के लिए वर्कर का नाम चुनें।")}>
                                    <option value="">Select Worker</option>
                                    {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                </select>
                            </div>
                        )}
                        {searchType === 'loom' && (
                            <div>
                                <label style={styles.fieldLabel}>Loom</label>
                                <select style={styles.input} value={selectedId} onChange={e=>{ setSelectedId(e.target.value); handleSelectVoice(e.target.value, " लूम नंबर"); }} onFocus={() => handleFocusVoice(" सर्च करने के लिए लूम नंबर चुनें।")}>
                                    <option value="">Select Loom</option>
                                    {looms.length > 0 ? looms.map(l => <option key={l.loom_id} value={l.loom_id}>Loom {String(l.loom_id).padStart(2, '0')}</option>) : <option disabled>Loading...</option>}
                                </select>
                            </div>
                        )}
                        <button onClick={(e) => handleVoiceClick(e, " रिकॉर्ड सर्च किया जा रहा है।", handleSearch)} style={{...styles.premiumButtonStyle, marginTop: '8px'}}>{loading ? 'Searching...' : 'Search Record'}</button>
                    </div>
                </div>

                <div>
                    {loading && <div style={{textAlign:'center', padding:'16px', color:'#111', fontWeight:'500', fontSize:'11px'}}>Loading Records...</div>}
                    {groupedData && groupedData.length === 0 && !loading && <p style={{textAlign:'center', color:'#333', fontWeight:'400', fontSize:'11px'}}>No records found.</p>}
                    {groupedData && groupedData.length > 0 && groupedData.map((group, index) => {
                        const dateObj = new Date(group.date);
                        const dayName = dateObj.toLocaleDateString('en-IN', { weekday: 'short' });
                        const dateName = dateObj.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                        const isNight = group.shift === 'Night';
                        const badgeBg = isNight ? '#0F172A' : '#f8fafc';
                        const badgeText = isNight ? '#FFF' : '#0F172A';

                        return (
                            <div key={index} style={{ background:'#ffffff', borderRadius:'16px', border:'1px solid #e2e8f0', overflow:'hidden', marginBottom:'12px', boxShadow: '0 4px 10px rgba(0,0,0,0.02)' }}>
                                <div style={{ padding:'12px 16px', borderBottom:'1px dashed #e2e8f0', background: '#f8fafc' }}>
                                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                            <Calendar size={14} color="#64748B"/>
                                            <span style={{ fontSize:'12px', fontWeight:'600', color:'#0F172A' }}>{dayName}, {dateName}</span>
                                            <span style={{ fontSize:'9px', padding:'4px 8px', borderRadius:'8px', fontWeight:'700', background: badgeBg, color: badgeText, border: '1px solid #e2e8f0' }}>{isNight ? 'NIGHT' : 'DAY'}</span>
                                        </div>
                                        <div style={{ textAlign:'right' }}>
                                            <div style={{ fontSize:'14px', fontWeight:'700', color:'#0F172A' }}>{group.total.toFixed(0)} <span style={{fontSize:'10px', fontWeight:'600', color:'#64748B'}}>Meter</span></div>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ padding:'16px' }}>
                                    {group.isOldDateApi ? (
                                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                                            {group.items.map((item, i) => (
                                                <React.Fragment key={i}>
                                                    <div style={{ background:'#f8fafc', padding:'12px', borderRadius:'12px', border:'1px solid #e2e8f0', textAlign:'center' }}>
                                                        <div style={{ fontSize:'9px', color:'#64748B', fontWeight:'600', textTransform: 'uppercase' }}>DAY</div>
                                                        <div style={{ fontSize:'14px', fontWeight:'700', color:'#0F172A' }}>{parseFloat(item.day).toFixed(0)}</div>
                                                    </div>
                                                    <div style={{ background:'#f8fafc', padding:'12px', borderRadius:'12px', border:'1px solid #e2e8f0', textAlign:'center' }}>
                                                        <div style={{ fontSize:'9px', color:'#64748B', fontWeight:'600', textTransform: 'uppercase' }}>NIGHT</div>
                                                        <div style={{ fontSize:'14px', fontWeight:'700', color:'#0F172A' }}>{parseFloat(item.night).toFixed(0)}</div>
                                                    </div>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'8px' }}>
                                            {group.items.map((item, i) => {
                                                const label = searchType === 'loom' ? (item.worker_name || 'Unknown') : `Loom ${String(item.loom_id).padStart(2, '0')}`;
                                                return (
                                                    <div key={i} style={{ background:'#f8fafc', padding:'10px 4px', borderRadius:'12px', border:'1px solid #e2e8f0', textAlign:'center' }}>
                                                        <div style={{ fontSize:'9px', color:'#64748B', fontWeight:'600', marginBottom: '2px' }}>{label}</div>
                                                        <div style={{ fontSize:'13px', fontWeight:'700', color:'#0F172A' }}>{parseFloat(item.meters).toFixed(0)}</div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </Modal>
    );
};

const SettingsPage = ({ globalGuide }) => {
  const { handleVoiceClick, handleFocusVoice, speakHinglish } = globalGuide;
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    textile_name: '',
    owner_name: '',
    mobile: '',
    email: '',
    address: '',
    gstin: '',
    total_looms: 0,
    bank_details: '',
    upi_id: '',
    shift_day_time: '08:00 AM - 08:00 PM',
    shift_night_time: '08:00 PM - 08:00 AM'
  });

  // Database se data load karna (Aapki existing get_owner_profile API se)
  useEffect(() => {
    fetch(`${API_BASE}/get_owner_profile.php`)
      .then(res => res.json())
      .then(json => {
        if(json.status === 'success') setSettings(json.data);
      });
  }, []);

  const handleSave = async (e) => {
    setLoading(true);
    try {
      // YAHAN APNI PURANI FILE KA NAAM LIKHEIN
      const res = await fetch(`${API_BASE}/update_owner_profile.php`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      const data = await res.json();
      if(data.status === 'success') {
        speakHinglish("प्रोफाइल और सेटिंग्स सफलतापूर्वक सेव हो गयी हैं।");
        alert("Success: All data saved to Database!");
      } else {
        alert("Error: " + data.message);
      }
    } catch (err) {
      alert("Network Error: Could not connect to database");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', paddingBottom: '100px', animation: 'fadeIn 0.5s' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#111', marginBottom: '20px' }}>Professional Control Center</h2>
      
      {/* 1. FACTORY IDENTITY (EXISTING FIELDS) */}
      <div style={styles.card}>
        <div style={{ padding: '12px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(37,99,235,0.05)' }}>
          <Building2 size={16} color="#2563EB" />
          <span style={{ fontWeight: '600', fontSize: '11px', color: '#2563EB' }}>FACTORY & OWNER INFO</span>
        </div>
        <div style={{ padding: '15px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={styles.row}>
            <div>
              <label style={styles.fieldLabel}>Textile Name</label>
              <input style={styles.input} value={settings.textile_name} onChange={e => setSettings({...settings, textile_name: e.target.value})} />
            </div>
            <div>
              <label style={styles.fieldLabel}>Owner Name</label>
              <input style={styles.input} value={settings.owner_name} onChange={e => setSettings({...settings, owner_name: e.target.value})} />
            </div>
          </div>
          <div style={styles.row}>
            <div>
              <label style={styles.fieldLabel}>Mobile</label>
              <input style={styles.input} value={settings.mobile} onChange={e => setSettings({...settings, mobile: e.target.value})} />
            </div>
            <div>
              <label style={styles.fieldLabel}>GSTIN</label>
              <input style={styles.input} value={settings.gstin} onChange={e => setSettings({...settings, gstin: e.target.value})} />
            </div>
          </div>
        </div>
      </div>

      {/* 2. SHIFT & LOOM CONFIG (DATABASE CONNECTED) */}
      <div style={{ ...styles.card, marginTop: '15px' }}>
        <div style={{ padding: '12px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(16,185,129,0.05)' }}>
          <Clock size={16} color="#10B981" />
          <span style={{ fontWeight: '600', fontSize: '11px', color: '#10B981' }}>OPERATIONAL SETTINGS</span>
        </div>
        <div style={{ padding: '15px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={styles.row}>
            <div>
              <label style={styles.fieldLabel}>Day Shift</label>
              <input style={styles.input} value={settings.shift_day_time} onChange={e => setSettings({...settings, shift_day_time: e.target.value})} />
            </div>
            <div>
              <label style={styles.fieldLabel}>Night Shift</label>
              <input style={styles.input} value={settings.shift_night_time} onChange={e => setSettings({...settings, shift_night_time: e.target.value})} />
            </div>
          </div>
          <div>
            <label style={styles.fieldLabel}>Total Looms in Factory</label>
            <input type="number" style={styles.input} value={settings.total_looms} onChange={e => setSettings({...settings, total_looms: e.target.value})} />
          </div>
        </div>
      </div>

      {/* 3. PAYMENT & BANKING (NEW FIELDS) */}
      <div style={{ ...styles.card, marginTop: '15px' }}>
        <div style={{ padding: '12px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(124,58,237,0.05)' }}>
          <Wallet size={16} color="#7C3AED" />
          <span style={{ fontWeight: '600', fontSize: '11px', color: '#7C3AED' }}>PAYMENT & BANK DETAILS</span>
        </div>
        <div style={{ padding: '15px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={styles.fieldLabel}>Primary UPI ID</label>
            <input placeholder="example@okicici" style={styles.input} value={settings.upi_id} onChange={e => setSettings({...settings, upi_id: e.target.value})} />
          </div>
          <div>
            <label style={styles.fieldLabel}>Bank Account / Branch Details</label>
            <textarea style={{...styles.input, height: '60px', resize: 'none'}} value={settings.bank_details} onChange={e => setSettings({...settings, bank_details: e.target.value})} />
          </div>
        </div>
      </div>

      <button 
        onClick={(e) => handleVoiceClick(e, "पूरी प्रोफाइल और सेटिंग्स अपडेट करने के लिए यहाँ दोबारा क्लिक करें।", handleSave, 'btn-save-all')}
        disabled={loading}
        style={{
          width: '100%', padding: '14px', borderRadius: '12px', background: '#111', color: 'white',
          fontWeight: '700', border: 'none', marginTop: '20px', cursor: 'pointer',
          boxShadow: '0 6px 15px rgba(0,0,0,0.2)'
        }}
      >
        {loading ? 'SAVING DATA...' : 'UPDATE PROFILE & SETTINGS'}
      </button>
    </div>
  );
};


// NAYA COMPONENT: PREMIUM LOOM POPUP MODAL (SCREENSHOT IDENTICAL SYSTEM)
const LoomPopupModal = ({ data, appBg, activeShift, onClose }) => {
    // 🌟 Hooks ko hamesha component ke absolute top par rakhein (Early return se pehle)
    const [popupFilter, setPopupFilter] = React.useState(data?.initialFilter || 'today');

    const handleClose = () => {
        if(window.speechSynthesis) window.speechSynthesis.cancel();
        onClose(); 
    };

    if (!data) return null;

    const loom = data.loom;
    const baseMeters = parseFloat(loom ? (loom.meters || 0) : 0);
    
    let history = { week: 0, week_day: 0, week_night: 0, month: 0, month_day: 0, month_night: 0, total: 0, total_day: 0, total_night: 0 };
    if (Array.isArray(data.loomHistory)) {
        const found = data.loomHistory.find(h => String(h.loom_id).replace(/[^0-9]/g, '') === String(data.id));
        if (found) history = {...history, ...found};
    } else if (typeof data.loomHistory === 'object' && data.loomHistory !== null) {
        const foundObj = data.loomHistory[data.id] || data.loomHistory[String(data.id)] || data.loomHistory[String(data.id).padStart(2, '0')];
        if (foundObj) history = {...history, ...foundObj};
    }

    const hw = parseFloat(history.week || 0); const hwd = parseFloat(history.week_day || 0); const hwn = parseFloat(history.week_night || 0);
    const hm = parseFloat(history.month || 0); const hmd = parseFloat(history.month_day || 0); const hmn = parseFloat(history.month_night || 0);
    const ht = parseFloat(history.total || 0); const htd = parseFloat(history.total_day || 0); const htn = parseFloat(history.total_night || 0);

    let todayDay = (loom && loom.day_meters > 0) ? loom.day_meters : (activeShift === 'day' ? baseMeters : 0);
    let todayNight = (loom && loom.night_meters > 0) ? loom.night_meters : (activeShift === 'night' ? baseMeters : 0);

    let filterTotal = baseMeters; 
    let shiftDay = todayDay;
    let shiftNight = todayNight;

    if (popupFilter === 'week') { filterTotal = hw + baseMeters; shiftDay = hwd + todayDay; shiftNight = hwn + todayNight; }
    else if (popupFilter === 'month') { filterTotal = hm + baseMeters; shiftDay = hmd + todayDay; shiftNight = hmn + todayNight; }
    else if (popupFilter === 'total') { filterTotal = ht + baseMeters; shiftDay = htd + todayDay; shiftNight = htn + todayNight; }

    let activeDateStr = data.todayStr;
    if (popupFilter === 'week') activeDateStr = data.weekStr;
    else if (popupFilter === 'month') activeDateStr = data.monthStr;
    else if (popupFilter === 'total') activeDateStr = data.totalStr;

    let currentActiveDays = popupFilter === 'today' ? (baseMeters > 0 ? 1 : 0) : (data.exactActiveDays ? (data.exactActiveDays[popupFilter] || 0) : 0);
    
    let productionLabel = "TOTAL PRODUCTION";
    if (popupFilter === 'today') productionLabel = "TODAY'S PRODUCTION";
    else if (popupFilter === 'week') productionLabel = "THIS WEEK'S PRODUCTION";
    else if (popupFilter === 'month') productionLabel = "THIS MONTH'S PRODUCTION";

    return (
        <Modal 
            isOpen={true} 
            onClose={handleClose} 
            title={
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', paddingBottom: '2px' }}>
                        <span style={{ fontSize: '15px', fontWeight: '900', color: '#0F172A', lineHeight: '1' }}>LOOM {String(data.id).padStart(2, '0')}</span>
                        <span style={{ fontSize: '9px', fontWeight: '700', color: '#64748B', letterSpacing: '0.8px', textTransform: 'uppercase' }}>OVERVIEW</span>
                    </div>
                    <div style={{ position: 'absolute', right: '56px', top: '18px' }}>
                        {data.isActive ? (
                            <div style={{ background: '#D1FAE5', padding: '4px 8px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '5px', border: '1px solid #A7F3D0' }}>
                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10B981', boxShadow: '0 0 6px #10B981' }}></span>
                                <span style={{fontSize: '8px', fontWeight: '800', color: '#059669', letterSpacing: '0.5px'}}>LIVE</span>
                            </div>
                        ) : (
                            <span style={{ fontSize: '8px', fontWeight: '800', color: '#64748B', background: '#E2E8F0', padding: '4px 8px', borderRadius: '8px', letterSpacing: '0.5px', border: '1px solid #CBD5E1' }}>OFFLINE</span>
                        )}
                    </div>
                </div>
            }
            icon={Factory} 
            iconColor="#0F172A"
            iconBg="#F1F5F9"
            maxWidth="100%"
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', textTransform: 'uppercase', width: '100%' }}>
                
                {/* 📊 SCREENSHOT MATCHING TOGGLE BUTTONS SYSTEM */}
                <div style={{ 
                    display: 'flex', 
                    background: '#F1F5F9', 
                    padding: '4px', 
                    borderRadius: '16px', 
                    gap: '4px',
                    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.05)', 
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    alignItems: 'center' 
                }}>
                    {['today', 'week', 'month', 'total'].map((filter) => {
                        const isActiveFilter = popupFilter === filter;
                        const labelMap = { today: 'TODAY', week: 'THIS WEEK', month: 'THIS MONTH', total: 'TOTAL' };
                        
                        return (
                            <button 
                                key={filter} 
                                onClick={() => setPopupFilter(filter)} 
                                style={isActiveFilter ? {
                                    flex: 1.2, 
                                    padding: '10px 4px', 
                                    background: '#FFFFFF', 
                                    border: '1px solid rgba(0,0,0,0.04)', 
                                    borderRadius: '12px', 
                                    fontWeight: '700', 
                                    color: '#0F172A', 
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)', 
                                    cursor: 'pointer', 
                                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', 
                                    display: 'flex', 
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                } : {
                                    flex: 1, 
                                    padding: '6px 4px', 
                                    background: 'transparent', 
                                    border: '1px solid transparent', 
                                    borderRadius: '12px', 
                                    fontWeight: '600', 
                                    color: '#64748B', 
                                    cursor: 'pointer', 
                                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', 
                                    display: 'flex', 
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    transform: 'scale(0.92)'
                                }}
                            >
                                <span style={{ fontSize: '9px', fontWeight: '800', letterSpacing: '0.5px' }}>
                                    {labelMap[filter]}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* 🌟 FULL WIDTH PRODUCTION CARD WITH INTEGRATED DATE & ACTIVE DAYS STRIP */}
                <div style={{ width: '100%' }}>
                    <div style={{ background: '#ffffff', borderRadius: '16px', padding: '14px 16px', boxShadow: '0 4px 10px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                        
                        {/* Card Header: Icon, Label & Embedded Date Strip (TOP ROW) */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ background: 'linear-gradient(135deg, #E0F2FE, #7DD3FC)', padding: '8px', borderRadius: '10px', boxShadow: '0 2px 6px rgba(2, 132, 199, 0.15)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Layers size={16} color="#0284C7"/>
                                </div>
                                <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {productionLabel}
                                </span>
                            </div>
                            
                         {/* DATE STRIP INSIDE THE BOX (TOP RIGHT) - MADE SMALLER */}
<span style={{ fontSize: '8px', color: '#475569', fontWeight: '700', background: '#F8FAFC', padding: '2px 6px', borderRadius: '6px', border: '1px solid #E2E8F0', display: 'inline-flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap' }}>
    <Calendar size={9} color="#64748B" /> 
    {activeDateStr.split(/-TO-| TO /).length === 2 ? (
        <>
            {activeDateStr.split(/-TO-| TO /)[0]}
            <span style={{ opacity: 0.35, margin: '0 4px', fontWeight: '500' }}>TO</span>
            {activeDateStr.split(/-TO-| TO /)[1]}
        </>
    ) : activeDateStr}
</span>
                        </div>
                        
                        {/* Production Value Section & Active Days Strip (BOTTOM ROW) */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%', marginTop: '2px' }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                <span style={{ fontSize: '24px', fontWeight: '800', color: '#0F172A', lineHeight: '1' }}>
                                    {filterTotal.toFixed(1)}
                                </span>
                                <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '700', letterSpacing: '0.5px' }}>
                                    METER
                                </span>
                            </div>
                            
                            {/* ACTIVE DAYS STRIP INSIDE THE BOX (BOTTOM RIGHT) */}
                            <span style={{ fontSize: '9px', color: '#059669', fontWeight: '700', background: '#E6FDF5', padding: '4px 10px', borderRadius: '8px', border: '1px solid #A7F3D0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <Activity size={10} color="#059669" /> {currentActiveDays} Active Days
                            </span>
                        </div>
                        
                    </div>
                </div>

                {/* 🌟 SHIFTS GRID (DAY & NIGHT) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    
                    {/* DAY SHIFT BOX */}
                    <div style={{ background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', borderRadius: '16px', padding: '14px', boxShadow: '0 4px 10px rgba(0,0,0,0.02)', border: '1px solid rgba(15, 23, 42, 0.04)', display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                        <div style={{ background: 'linear-gradient(135deg, #FEF3C7, #FCD34D)', padding: '8px', borderRadius: '10px', boxShadow: '0 2px 6px rgba(217, 119, 6, 0.15)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Sun size={16} color="#B45309"/>
                        </div>
                        <div style={{display:'flex', flexDirection:'column', justifyContent:'center', flex:1}}>
                            <span style={{ fontSize: '7.5px', color: '#64748B', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                                DAY SHIFT
                            </span>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '2px' }}>
                                <span style={{ fontSize: '15px', fontWeight: '800', color: '#0F172A', lineHeight: '1' }}>
                                    {shiftDay.toFixed(1)}
                                </span>
                                <span style={{ fontSize: '8.5px', color: '#64748B', fontWeight: '700', letterSpacing: '0.5px' }}>
                                    METER
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    {/* NIGHT SHIFT BOX */}
                    <div style={{ background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', borderRadius: '16px', padding: '14px', boxShadow: '0 4px 10px rgba(0,0,0,0.02)', border: '1px solid rgba(15, 23, 42, 0.04)', display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                        <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '8px', borderRadius: '10px', boxShadow: '0 2px 6px rgba(79, 70, 229, 0.15)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Moon size={16} color="#4338CA"/>
                        </div>
                        <div style={{display:'flex', flexDirection:'column', justifyContent:'center', flex:1}}>
                            <span style={{ fontSize: '7.5px', color: '#64748B', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                                NIGHT SHIFT
                            </span>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '2px' }}>
                                <span style={{ fontSize: '15px', fontWeight: '800', color: '#0F172A', lineHeight: '1' }}>
                                    {shiftNight.toFixed(1)}
                                </span>
                                <span style={{ fontSize: '8.5px', color: '#64748B', fontWeight: '700', letterSpacing: '0.5px' }}>
                                    METER
                                </span>
                            </div>
                        </div>
                    </div>
                    
                </div>
            </div>
        </Modal>
    );
};

// --- COMPONENT: DASHBOARD VIEW (Owner) ---
const DashboardView = ({ shiftTimings, stats, looms, workers, setStatsTab, statsTab, setStatsModalData, onOpenProfile, onOpenSearch,onDownloadReport, loomHistory, appBg, setAppBg, notifOn, setNotifOn, voiceAssist, toggleVoiceAssist, setShowGuideModal, handleVoiceClick, speakHinglish, activeGuideFlow }) => {
    const activeStats = statsTab === 'month' ? stats.summaries.month : statsTab === 'total' ? stats.summaries.total : stats.summaries.week;
    const [loomTimeFilter, setLoomTimeFilter] = useState('today');
    const [activeShift, setActiveShift] = useState('day');
    
    const [selectedLoomPopup, setSelectedLoomPopup] = useState(null);
    const isAnyLoomActive = looms.some(loom => isLoomActive(loom.last_updated));

    // --- NEW WHATSAPP FEATURE STATE ---
    const [isSendingReport, setIsSendingReport] = useState(false);
    const [reportStatusMsg, setReportStatusMsg] = useState('');
    const shiftRef = useRef(activeShift); // To track shift transition for automated sending

    // --- NEW WHATSAPP TRIGGER FUNCTION (WITH ADVANCED ERROR HANDLING) ---
    const handleSendWhatsAppReport = async (shiftOverride = null) => {
        const shiftToSend = shiftOverride || activeShift;
        setIsSendingReport(true);
        setReportStatusMsg(`Sending ${shiftToSend.toUpperCase()} shift report via WhatsApp...`);
        if (voiceAssist) speakHinglish(` व्हाट्सएप पर ${shiftToSend} शिफ्ट की पूरी रिपोर्ट भेजी जा रही है।`);

        try {
            const res = await fetch(`${API_BASE}/send_whatsapp_report.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ shift: shiftToSend, date: new Date().toISOString().split('T')[0] })
            });
            
            // JSON की जगह पहले Text में रिस्पांस लेंगे ताकि क्रैश न हो
            const textResponse = await res.text(); 
            
            try {
                const data = JSON.parse(textResponse);
                if (data.status === 'success') {
                    setReportStatusMsg('Premium Report Sent Successfully! ✅');
                    if (voiceAssist) speakHinglish(' रिपोर्ट सफलतापूर्वक व्हाट्सएप पर भेज दी गई है।');
                } else {
                    setReportStatusMsg(`Failed: ${data.message} ❌`);
                    console.error(" Server Error Details:", data);
                }
            } catch (parseError) {
                // अगर PHP से JSON के बजाय कोई HTML या Warning आ रही है
                console.error("Invalid Server Response:", textResponse);
                alert("Server Error! Console चेक करें।\nResponse: " + textResponse.substring(0, 100));
                setReportStatusMsg('Server configuration error. ❌');
            }
        } catch(e) {
            console.error("Network/Fetch Error:", e);
            setReportStatusMsg('Network Error! Cannot reach server. ❌');
        }
        
        setTimeout(() => {
            setIsSendingReport(false);
            setReportStatusMsg('');
        }, 5000); // एरर पढने के लिए टाइम 4 से 5 सेकंड कर दिया है
    };

   useEffect(() => {
        const checkShift = () => {
            try {
                // 🌟 PERFECT TIMEZONE FIX FOR INDIA 🌟
                const nowStr = new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" });
                const nowIST = new Date(nowStr);
                const currentHour = nowIST.getHours();
                const currentMinute = nowIST.getMinutes();
                const currentMins = (currentHour * 60) + currentMinute;

                // 🌟 DYNAMIC MINUTES & HOURS PARSER 🌟
                const parseTimeStr = (timeStr, defaultHour, defaultMinute = 0) => {
                    if (!timeStr || typeof timeStr !== 'string') return (defaultHour * 60) + defaultMinute;
                    const match = timeStr.trim().match(/(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)?/i);
                    if (match) {
                        let h = parseInt(match[1], 10);
                        let m = match[2] ? parseInt(match[2], 10) : 0;
                        const ampm = match[3] ? match[3].toUpperCase() : null;
                        if (ampm === 'PM' && h < 12) h += 12;
                        if (ampm === 'AM' && h === 12) h = 0;
                        return (h * 60) + m;
                    }
                    return (defaultHour * 60) + defaultMinute;
                };

                const dayStartMins = parseTimeStr(shiftTimings?.day, 8, 0);   
                const nightStartMins = parseTimeStr(shiftTimings?.night, 20, 0); 
                
                let newShift = 'day';
                
                if (dayStartMins < nightStartMins) {
                    if (currentMins >= dayStartMins && currentMins < nightStartMins) newShift = 'day';
                    else newShift = 'night';
                } else {
                    if (currentMins >= dayStartMins || currentMins < nightStartMins) newShift = 'day';
                    else newShift = 'night';
                }
                
                if (shiftRef.current && shiftRef.current !== newShift) {
                    handleSendWhatsAppReport(shiftRef.current); 
                }
                
                shiftRef.current = newShift;
                setActiveShift(newShift);
            } catch (err) {
                console.error("Shift Calculation Error:", err);
                setActiveShift('day'); 
            }
        };
        
        checkShift();
        const interval = setInterval(checkShift, 60000); 
        return () => clearInterval(interval);
    }, [shiftTimings]);

    const today = new Date();
    const formatOpts = { day: 'numeric', month: 'long', year: 'numeric' };
const formatOptsShort = { day: 'numeric', month: 'long' };
    const todayStr = today.toLocaleDateString('en-IN', formatOpts);
    const currentDay = today.getDay(); 
    const diffToSat = (currentDay + 1) % 7; 
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - diffToSat);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
   const weekStr = `${startOfWeek.toLocaleDateString('en-IN', formatOpts)} TO ${endOfWeek.toLocaleDateString('en-IN', formatOpts)}`;
   const weekToggleStr = `${startOfWeek.toLocaleDateString('en-IN', formatOptsShort)} TO ${endOfWeek.toLocaleDateString('en-IN', formatOptsShort)}`;

    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
   const monthStr = `${startOfMonth.toLocaleDateString('en-IN', formatOpts)} TO ${endOfMonth.toLocaleDateString('en-IN', formatOpts)}`;
   const monthToggleStr = `${startOfMonth.toLocaleDateString('en-IN', formatOptsShort)} TO ${endOfMonth.toLocaleDateString('en-IN', formatOptsShort)}`;

// 🌟 DYNAMIC START DATE CALCULATION (FOR TOGGLE AND STATS BOXES) 🌟
    let allRecordDates = [];
    if (stats?.reports?.daily_wise) {
        Object.values(stats.reports.daily_wise).forEach(yearData => {
            Object.values(yearData).forEach(monthData => {
                Object.keys(monthData).forEach(dateStr => {
                    allRecordDates.push(dateStr);
                });
            });
        });
    }

    let oldestDateStr = today.toLocaleDateString('en-IN', formatOpts); 
    let oldestDateStrShort = today.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

    if (allRecordDates.length > 0) {
        allRecordDates.sort(); // Sabse purani (First) date nikalne ke liye
        const oldest = new Date(allRecordDates[0]);
        if (!isNaN(oldest)) {
            oldestDateStr = oldest.toLocaleDateString('en-IN', formatOpts);
            oldestDateStrShort = oldest.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
        }
    }

    const totalStr = `${oldestDateStr} TO ${todayStr}`;
const totalToggleStr = `${oldestDateStrShort} TO Till Now`;
    const todayToggleStr = today.toLocaleDateString('en-IN', formatOptsShort);

    // 🌟 PERFECT DATE SYNC LOGIC FOR STATS BOXES 🌟
    const activeDateStr = loomTimeFilter === 'today' ? todayStr : loomTimeFilter === 'week' ? weekStr : loomTimeFilter === 'month' ? monthStr : totalStr;

    const filterData = {
        today: { label: 'Today', date: todayToggleStr }, week: { label: 'Week', date: weekToggleStr },
        month: { label: 'Month', date: monthToggleStr }, total: { label: 'Total', date: totalToggleStr }
    };

    const getHeaderDateString = () => {
        switch(loomTimeFilter) { case 'week': return weekStr; case 'month': return monthStr; case 'total': return totalStr; case 'today': default: return todayStr; }
    };

    const todayStats = useMemo(() => {
        let day = 0, night = 0, total = 0;
        workers.forEach(w => {
            const prod = parseFloat(w.today || 0);
            if (prod > 0) { if (w.shift === 'Day') day += prod; else night += prod; total += prod; }
        });
        return { day, night, total }; 
    }, [workers]);

    // 🌟 ACTIVE DAYS STRICT CALCULATION LOGIC (ONLY COUNT IF TOTAL > 0) 🌟
    const exactActiveDays = useMemo(() => {
        let countWeek = 0, countMonth = 0, countTotal = 0;
        
        // 1. Week Active Days Calculation
        if (stats?.weekly) {
            Object.values(stats.weekly).forEach(day => { 
                if (parseFloat(day.Total || 0) > 0) countWeek++; 
            });
        }
        
        // 2. Month Active Days Calculation
        const todayObj = new Date();
        const currentYearStr = todayObj.getFullYear().toString();
        const ALL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const currentMonthStr = ALL_MONTHS[todayObj.getMonth()];
        const monthData = stats?.reports?.daily_wise?.[currentYearStr]?.[currentMonthStr];
        
        if (monthData) {
            Object.values(monthData).forEach(day => { 
                if (parseFloat(day.Total || 0) > 0) countMonth++; 
            });
        }
        
        // 3. Total Active Days Calculation
        if (stats?.reports?.daily_wise) {
            Object.values(stats.reports.daily_wise).forEach(yearData => {
                Object.values(yearData).forEach(mData => {
                    Object.values(mData).forEach(day => { 
                        if (parseFloat(day.Total || 0) > 0) countTotal++; 
                    });
                });
            });
        }
        
        return { week: countWeek, month: countMonth, total: countTotal };
    }, [stats]);

    // Override Backend values with strict Frontend calculation for UI & Voice sync
    if (stats?.summaries?.week) stats.summaries.week.ActiveDays = exactActiveDays.week;
    if (stats?.summaries?.month) stats.summaries.month.ActiveDays = exactActiveDays.month;
    if (stats?.summaries?.total) stats.summaries.total.ActiveDays = exactActiveDays.total;

    const loomActiveDays = loomTimeFilter === 'today' ? (todayStats.total > 0 ? 1 : 0) : exactActiveDays[loomTimeFilter] || 0;

    const monthNamesVoice = [" जनवरी", " फरवरी", " मार्च", " अप्रैल", " मई", " जून", " जुलाई", " अगस्त", " सितंबर", " अक्टूबर", " नवंबर", " दिसंबर"];
    
    const formatVoiceDate = (dateObj) => {
        if (!dateObj || isNaN(dateObj.getTime())) return "";
        return `${dateObj.getDate()} ${monthNamesVoice[dateObj.getMonth()]}`;
    };

    const todayVoiceDate = `${today.getDate()} ${monthNamesVoice[today.getMonth()]} ${today.getFullYear()}`;
    
    // --- TODAY VOICE MSG ---
    let todayVoiceMsg = "";
    if (todayStats.total > 0) {
        todayVoiceMsg = ` आज की तारीख ${todayVoiceDate} का, आपके टेक्सटाइल कारखाने में टोटल प्रोडक्शन ${todayStats.total.toFixed(0)} मीटर हो चुका है, जिसमें `;
        if (activeShift.trim() === 'day') {
            todayVoiceMsg += ` रात की शिफ्ट का प्रोडक्शन ${todayStats.night.toFixed(0)} मीटर हुआ है और दिन की शिफ्ट का प्रोडक्शन ${todayStats.day.toFixed(0)} मीटर चल रहा है।`;
        } else {
            todayVoiceMsg += ` दिन की शिफ्ट का प्रोडक्शन ${todayStats.day.toFixed(0)} मीटर हुआ है और रात की शिफ्ट का प्रोडक्शन ${todayStats.night.toFixed(0)} मीटर चल रहा है।`;
        }
    } else {
        todayVoiceMsg = ` आज की तारीख ${todayVoiceDate} को कारखाने में प्रोडक्शन जीरो मीटर है।`;
    }

      // --- WEEK VOICE MSG ---
     const wStats = stats.summaries.week;
      let weekVoiceMsg = "";
     if (wStats.ActiveDays > 0 || wStats.Total > 0) {
        weekVoiceMsg = ` इस हफ्ते ${formatVoiceDate(startOfWeek)} से ${formatVoiceDate(endOfWeek)} तक, आपके टेक्सटाइल कारखाने में टोटल ${wStats.ActiveDays} दिन वर्कर्स ने ड्यूटी कि है, और आपके टेक्सटाइल कारखाने का टोटल प्रोडक्शन ${wStats.Total.toFixed(0)} मीटर हुआ है, इसमें `;
        if (activeShift.trim() === 'day') {
            weekVoiceMsg += ` रात की शिफ्ट में ${wStats.Night.toFixed(0)} मीटर प्रोडक्शन हुआ है और दिन की शिफ्ट में ${wStats.Day.toFixed(0)} मीटर चल रहा है।`;
        } else {
            weekVoiceMsg += ` दिन की शिफ्ट में ${wStats.Day.toFixed(0)} मीटर प्रोडक्शन हुआ है और रात की शिफ्ट में ${wStats.Night.toFixed(0)} मीटर चल रहा है।`;
        }
     } else {
        weekVoiceMsg = ` इस हफ्ते ${formatVoiceDate(startOfWeek)} से ${formatVoiceDate(endOfWeek)} तक, आपके टेक्सटाइल कारखाने में वर्कर्स ने एक भी दिन ड्यूटी नहीं कि है इसलिए प्रोडक्शन जीरो मीटर है।`;
     }

     // --- MONTH VOICE MSG ---
     const mStats = stats.summaries.month;
     let monthVoiceMsg = "";
     if (mStats.ActiveDays > 0 || mStats.Total > 0) {
        monthVoiceMsg = ` इस महीने ${formatVoiceDate(startOfMonth)} से ${formatVoiceDate(endOfMonth)} तक, आपके टेक्सटाइल कारखाने में टोटल ${mStats.ActiveDays} दिन वर्कर्स ने ड्यूटी कि है, और आपके टेक्सटाइल कारखाने का टोटल प्रोडक्शन ${mStats.Total.toFixed(0)} मीटर हुआ है, इसमें `;
        if (activeShift.trim() === 'day') {
            monthVoiceMsg += ` रात की शिफ्ट में ${mStats.Night.toFixed(0)} मीटर प्रोडक्शन हुआ है और दिन की शिफ्ट में ${mStats.Day.toFixed(0)} मीटर चल रहा है।`;
        } else {
            monthVoiceMsg += ` दिन की शिफ्ट में ${mStats.Day.toFixed(0)} मीटर प्रोडक्शन हुआ है और रात की शिफ्ट में ${mStats.Night.toFixed(0)} मीटर चल रहा है।`;
        }
     } else {
        monthVoiceMsg = ` इस महीने ${formatVoiceDate(startOfMonth)} से ${formatVoiceDate(endOfMonth)} तक, आपके टेक्सटाइल कारखाने में वर्कर्स ने एक भी दिन ड्यूटी नहीं कि है इसलिए प्रोडक्शन जीरो मीटर है।`;
     }

     // --- TOTAL VOICE MSG ---
     let oldestVoiceDate = formatVoiceDate(today); 
     if (allRecordDates && allRecordDates.length > 0) {
        const old = new Date(allRecordDates[0]);
        if (!isNaN(old)) oldestVoiceDate = formatVoiceDate(old);
     }
    
     const tStats = stats.summaries.total;
     let totalVoiceMsg = "";
     if (tStats.ActiveDays > 0 || tStats.Total > 0) {
        totalVoiceMsg = ` टोटल ${oldestVoiceDate} से अब तक यानी ${formatVoiceDate(today)} तक, आपके टेक्सटाइल कारखाने में टोटल ${tStats.ActiveDays} दिन वर्कर्स ने ड्यूटी कि है, और आपके टेक्सटाइल कारखाने का टोटल प्रोडक्शन ${tStats.Total.toFixed(0)} मीटर हुआ है, इसमें `;
        if (activeShift.trim() === 'day') {
            totalVoiceMsg += ` रात की शिफ्ट में ${tStats.Night.toFixed(0)} मीटर प्रोडक्शन हुआ है और दिन की शिफ्ट में ${tStats.Day.toFixed(0)} मीटर चल रहा है।`;
        } else {
            totalVoiceMsg += ` दिन की शिफ्ट में ${tStats.Day.toFixed(0)} मीटर प्रोडक्शन हुआ है और रात की शिफ्ट में ${tStats.Night.toFixed(0)} मीटर चल रहा है।`;
        }
     } else {
        totalVoiceMsg = ` टोटल ${oldestVoiceDate} से अब तक यानी ${formatVoiceDate(today)} तक, आपके टेक्सटाइल कारखाने में वर्कर्स ने एक भी दिन ड्यूटी नहीं कि है इसलिए प्रोडक्शन जीरो मीटर है।`;
     }
    
     // ASSIGNED LOOMS TOGGLE VOICE MESSAGES (Perfect Match with UI for Owner)
     const loomVoiceMsgs = {
        today: todayStats.total > 0 
            ? ` आज के दिन ${formatVoiceDate(today)} को आपके टेक्सटाइल कारखाने में टोटल प्रोडक्शन ${todayStats.total.toFixed(0)} मीटर हुआ है, जिसे नीचे लूम के हिसाब से दिखाया गया है।`
            : ` आज के दिन ${formatVoiceDate(today)} को प्रोडक्शन जीरो मीटर है।`,
        week: wStats.Total > 0
            ? ` इस हफ्ते ${formatVoiceDate(startOfWeek)} से ${formatVoiceDate(endOfWeek)} तक, आपके टेक्सटाइल कारखाने में टोटल ${wStats.ActiveDays} दिन ड्यूटी हुई है, और आपका टोटल प्रोडक्शन ${wStats.Total.toFixed(0)} मीटर हुआ है, जिसे नीचे लूम के हिसाब से दिखाया गया है।`
            : ` इस हफ्ते ${formatVoiceDate(startOfWeek)} से ${formatVoiceDate(endOfWeek)} तक, किसी भी वर्कर ने ड्यूटी नहीं की है इसलिए प्रोडक्शन जीरो मीटर है।`,
        month: mStats.Total > 0
            ? ` इस महीने ${formatVoiceDate(startOfMonth)} से ${formatVoiceDate(endOfMonth)} तक, आपके टेक्सटाइल कारखाने में टोटल ${mStats.ActiveDays} दिन ड्यूटी हुई है, और आपका टोटल प्रोडक्शन ${mStats.Total.toFixed(0)} मीटर हुआ है, जिसे नीचे लूम के हिसाब से दिखाया गया है।`
            : ` इस महीने ${formatVoiceDate(startOfMonth)} से ${formatVoiceDate(endOfMonth)} तक, किसी भी वर्कर ने ड्यूटी नहीं की है इसलिए प्रोडक्शन जीरो मीटर है।`,
        total: tStats.Total > 0
            ? ` टोटल ${oldestVoiceDate} से अब तक यानी ${formatVoiceDate(today)} तक, आपके टेक्सटाइल कारखाने में टोटल ${tStats.ActiveDays} दिन ड्यूटी हुई है, और आपका टोटल प्रोडक्शन ${tStats.Total.toFixed(0)} मीटर हुआ है, जिसे नीचे लूम के हिसाब से दिखाया गया है।`
            : ` टोटल प्रोडक्शन ${oldestVoiceDate} से ${formatVoiceDate(today)} तक, किसी भी वर्कर ने ड्यूटी नहीं की है इसलिए प्रोडक्शन जीरो मीटर है।`
     };
    
     return (
        <>
            {/* WHATSAPP SENDING NOTIFICATION POPUP */}
            {isSendingReport && (
                <div style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 100000, background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', border: '1px solid #25D366', padding: '12px 24px', borderRadius: '24px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 10px 30px rgba(37, 211, 102, 0.3)', animation: 'zoomIn 0.3s ease' }}>
                    <MessageCircle size={24} color="#25D366" className={reportStatusMsg.includes('Sending') ? 'pulse-anim' : ''} />
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#111' }}>{reportStatusMsg}</span>
                    <style>{`.pulse-anim { animation: pulseGuide 1.5s infinite; }`}</style>
                </div>
            )}

           {/* ===== 🌟 PREMIUM HEADER 🌟 ===== */}
            <Header 
                onOpenSearch={onOpenSearch} 
                onOpenProfile={onOpenProfile} 
                handleSendWhatsAppReport={handleSendWhatsAppReport} 
                activeShift={activeShift} 
                handleVoiceClick={handleVoiceClick} 
                onDownloadReport={onDownloadReport} // 👈 Added prop connectivity function
            />

            <div style={styles.content}>
                <div style={{display:'flex', flexDirection:'column', gap:'12px', marginBottom:'20px'}}>
                    
                 {/* ===== 🌟 1000x ULTRA-PREMIUM SOLID COMBINED CARD (Platinum & Slate) 🌟 ===== */}
                    <div className="animate-in" style={{ 
                        background: '#FFFFFF', 
                        border: '1px solid #F1F5F9', 
                        borderRadius: '24px', 
                        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04), 0 2px 8px rgba(15, 23, 42, 0.02)', 
                        overflow: 'hidden' 
                    }}>
                        <div style={{ padding: '14px' }}>
                            
                            {/* 1. SINGLE SET OF TOGGLE BUTTONS (SOLID PLATINUM STYLE) */}
                            <div style={{ 
                                display: 'flex', 
                                background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)', 
                                padding: '6px', 
                                borderRadius: '16px', 
                                gap: '4px',
                                marginBottom: '16px', 
                                boxShadow: 'inset 0 2px 5px rgba(15,23,42,0.02)', 
                                border: '1px solid #E2E8F0',
                                overflowX: 'auto', 
                                scrollbarWidth: 'none',
                                alignItems: 'center' 
                            }}>
                                {Object.keys(filterData).map((filter) => {
                                    const isActiveFilter = loomTimeFilter === filter;
                                    
                                    const introMsgs = {
                                        today: " ये टुडे बटन है, इसमें आप आज का टोटल प्रोडक्शन देख सकते है, देखने के लिए दोबारा क्लिक करें।",
                                        week: " ये दिस वीक बटन है, इसमें आप इस हफ्ते का टोटल प्रोडक्शन देख सकते है, देखने के लिए दोबारा क्लिक करें।",
                                        month: " ये दिस मंथ बटन है, इसमें आप इस महीने का टोटल प्रोडक्शन देख सकते है, देखने के लिए दोबारा क्लिक करें।",
                                        total: " ये टोटल बटन है, इसमें आप टोटल प्रोडक्शन देख सकते है, देखने के लिए दोबारा क्लिक करें।"
                                    };

                                    return (
                                        <button 
                                            key={filter} 
                                            onClick={(e) => {
                                                handleVoiceClick && handleVoiceClick(
                                                    e, 
                                                    introMsgs[filter], 
                                                    () => { 
                                                        setLoomTimeFilter(filter); 
                                                        setStatsTab(filter === 'today' ? 'week' : filter); 
                                                        if (voiceAssist && speakHinglish) speakHinglish(loomVoiceMsgs[filter]); 
                                                    }, 
                                                    filter === 'today' ? 'tab-stats-today' : `tab-stats-${filter}`,
                                                    isActiveFilter, 
                                                    loomVoiceMsgs[filter] 
                                                );
                                            }} 
                                            style={isActiveFilter ? {
                                                flex: 1.2, 
                                                minWidth: '75px', 
                                                padding: '10px 4px', 
                                                background: '#FFFFFF', 
                                                border: '1px solid #E2E8F0', 
                                                borderRadius: '12px', 
                                                fontWeight: '700', 
                                                color: '#0F172A', 
                                                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.05)', 
                                                cursor: 'pointer', 
                                                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', 
                                                display: 'flex', 
                                                flexDirection: 'column', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                animation: 'bubblePop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                                            } : {
                                                flex: 1, 
                                                minWidth: '60px', 
                                                padding: '6px 4px', 
                                                background: 'transparent', 
                                                border: '1px solid transparent', 
                                                borderRadius: '12px', 
                                                fontWeight: '600', 
                                                color: '#64748B', 
                                                cursor: 'pointer', 
                                                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', 
                                                display: 'flex', 
                                                flexDirection: 'column', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                transform: 'scale(0.92)'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 0' }}>
                                                <div style={{ 
                                                    fontSize: isActiveFilter ? '10px' : '9px', 
                                                    fontWeight: '800', 
                                                    textTransform: 'uppercase', 
                                                    letterSpacing: '0.5px', 
                                                    color: isActiveFilter ? '#0F172A' : '#64748B',
                                                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                                                }}>
                                                    {filter === 'week' ? 'THIS WEEK' : filter === 'month' ? 'THIS MONTH' : filterData[filter].label.toUpperCase()}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* 2. DYNAMIC TOTAL PRODUCTION SUMMARY (Solid Premium Look) */}
                            {(() => {
                                let currentTotal = 0, currentDay = 0, currentNight = 0, currentActiveDays = 0;
                                if (loomTimeFilter === 'today') { currentTotal = todayStats.total; currentDay = todayStats.day; currentNight = todayStats.night; currentActiveDays = todayStats.total > 0 ? 1 : 0; }
                                else if (loomTimeFilter === 'week') { currentTotal = stats?.summaries?.week?.Total || 0; currentDay = stats?.summaries?.week?.Day || 0; currentNight = stats?.summaries?.week?.Night || 0; currentActiveDays = stats?.summaries?.week?.ActiveDays || 0; }
                                else if (loomTimeFilter === 'month') { currentTotal = stats?.summaries?.month?.Total || 0; currentDay = stats?.summaries?.month?.Day || 0; currentNight = stats?.summaries?.month?.Night || 0; currentActiveDays = stats?.summaries?.month?.ActiveDays || 0; }
                                else if (loomTimeFilter === 'total') { currentTotal = stats?.summaries?.total?.Total || 0; currentDay = stats?.summaries?.total?.Day || 0; currentNight = stats?.summaries?.total?.Night || 0; currentActiveDays = stats?.summaries?.total?.ActiveDays || 0; }
                                
                                let productionLabel = "TOTAL PRODUCTION";
                                if (loomTimeFilter === 'today') productionLabel = "TODAY'S PRODUCTION";
                                else if (loomTimeFilter === 'week') productionLabel = "THIS WEEK'S PRODUCTION";
                                else if (loomTimeFilter === 'month') productionLabel = "THIS MONTH'S PRODUCTION";
                                
                                return (
                                    <div onClick={(e) => handleVoiceClick && handleVoiceClick(e, loomVoiceMsgs[loomTimeFilter], null)}>
                                        
                                        {/* 🌟 FULL WIDTH PRODUCTION CARD WITH INTEGRATED DATE & ACTIVE DAYS STRIP 🌟 */}
                                        <div style={{ width: '100%', marginBottom: '12px' }}>
                                            <div style={{ 
                                                background: '#FFFFFF', 
                                                borderRadius: '16px', 
                                                padding: '14px 16px', 
                                                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03)', 
                                                border: '1px solid #E2E8F0', 
                                                display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' 
                                            }}>
                                                
                                                {/* Card Header: Icon, Label & Embedded Date Strip */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <div style={{ background: 'linear-gradient(135deg, #E0F2FE, #7DD3FC)', padding: '8px', borderRadius: '10px', boxShadow: '0 2px 6px rgba(2, 132, 199, 0.15)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <Layers size={16} color="#0284C7"/>
                                                        </div>
                                                        <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                            {productionLabel}
                                                        </span>
                                                    </div>
                                                    
                                                    {/* DATE STRIP INSIDE THE BOX */}
                                                    <span style={{ fontSize: '9px', color: '#475569', fontWeight: '700', background: '#F8FAFC', padding: '4px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                        <Calendar size={10} color="#64748B" /> 
                                                        {activeDateStr.split(/\s+-\s+|\s+TO\s+|-TO-/).length === 2 ? (
                                                            <>
                                                                {activeDateStr.split(/\s+-\s+|\s+TO\s+|-TO-/)[0]}
                                                                <span style={{ fontSize: '9px', opacity: 0.35, margin: '0 5px', fontWeight: '500' }}>TO</span>
                                                                {activeDateStr.split(/\s+-\s+|\s+TO\s+|-TO-/)[1]}
                                                            </>
                                                        ) : activeDateStr}
                                                    </span>
                                                </div>
                                                
                                                {/* Production Value Section & Active Days Strip */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%', marginTop: '2px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
    <span style={{ fontSize: '24px', fontWeight: '800', color: '#0F172A', lineHeight: '1' }}>
        {currentTotal.toFixed(1)}
    </span>
    <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '700', letterSpacing: '0.5px', marginRight: '8px' }}>
        METER
    </span>
</div>
                                                    
                                                    {/* ACTIVE DAYS STRIP INSIDE THE BOX */}
                                                    <span style={{ fontSize: '9px', color: '#059669', fontWeight: '700', background: '#E6FDF5', padding: '4px 10px', borderRadius: '8px', border: '1px solid #A7F3D0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                        <Activity size={10} color="#059669" /> {currentActiveDays} Active Days
                                                    </span>
                                                </div>
                                                
                                            </div>
                                        </div>

                                      {/* 🌟 RETAINED: DAY & NIGHT SHIFT BOXES (UI upgraded to match Solid Premium Theme) 🌟 */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                            {/* Day Shift Box */}
                                            <div style={{ background: '#FFFFFF', borderRadius: '16px', padding: '14px', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03)', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                                                <div style={{ background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)', padding: '8px', borderRadius: '10px', border: '1px solid #E2E8F0', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Sun size={16} color="#334155" strokeWidth={2.5} />
                                                </div>
                                                <div style={{display:'flex', flexDirection:'column', justifyContent:'center', flex:1}}>
                                                    <span style={{ fontSize: '7.5px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                                                        DAY SHIFT
                                                    </span>
                                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '2px' }}>
                                                        <span style={{ fontSize: '15px', fontWeight: '800', color: '#0F172A', lineHeight: '1' }}>
                                                            {currentDay.toFixed(1)}
                                                        </span>
                                                        <span style={{ fontSize: '8.5px', color: '#64748B', fontWeight: '700', letterSpacing: '0.5px' }}>
                                                            METER
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Night Shift Box */}
                                            <div style={{ background: '#FFFFFF', borderRadius: '16px', padding: '14px', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03)', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                                                <div style={{ background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)', padding: '8px', borderRadius: '10px', border: '1px solid #E2E8F0', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Moon size={16} color="#334155" strokeWidth={2.5} />
                                                </div>
                                                <div style={{display:'flex', flexDirection:'column', justifyContent:'center', flex:1}}>
                                                    <span style={{ fontSize: '7.5px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                                                        NIGHT SHIFT
                                                    </span>
                                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '2px' }}>
                                                        <span style={{ fontSize: '15px', fontWeight: '800', color: '#0F172A', lineHeight: '1' }}>
                                                            {currentNight.toFixed(1)}
                                                        </span>
                                                        <span style={{ fontSize: '8.5px', color: '#64748B', fontWeight: '700', letterSpacing: '0.5px' }}>
                                                            METER
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 🌟 VIEW DETAILED REPORT BUTTON (WorkerHome Style) 🌟 */}
                                        {loomTimeFilter !== 'today' && (
                                            <div 
                                                onClick={(e) => {
                                                    let detailedReportVoiceMsg = "डिटेल्ड रिपोर्ट लोड हो रही है।";
                                                    let modalPeriod = loomTimeFilter;
                                                    if (modalPeriod === 'week') detailedReportVoiceMsg = " इस बटन पर क्लिक करके आप अपने कारखाने की पूरी डिटेल्ड प्रोडक्शन रिपोर्ट हफ्ते के हिसाब से देख सकते हैं।";
                                                    else if (modalPeriod === 'month') detailedReportVoiceMsg = " इस बटन पर क्लिक करके आप अपने कारखाने की पूरी डिटेल्ड प्रोडक्शन रिपोर्ट महीने के हिसाब से देख सकते हैं।";
                                                    else if (modalPeriod === 'total') detailedReportVoiceMsg = " इस बटन पर क्लिक करके आप अपने कारखाने की पूरी डिटेल्ड प्रोडक्शन रिपोर्ट साल और महीने के हिसाब से देख सकते हैं।";
                                                    
                                                    handleVoiceClick(e, detailedReportVoiceMsg, () => {
                                                        setStatsTab(modalPeriod);
                                                        setStatsModalData({ title: 'Detailed Production Report', type: 'report', data: stats, period: modalPeriod });
                                                    }, 'btn-detailed-report');
                                                }} 
                                                style={{marginTop:'12px', paddingTop:'12px', borderTop:'1px solid #F1F5F9', cursor: 'pointer', display:'flex', justifyContent:'center'}}
                                            >
                                                <span style={{ 
                                                    background: '#FFFFFF', 
                                                    border: '1px solid #E2E8F0', 
                                                    padding: '8px 16px', 
                                                    borderRadius: '14px', 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: '6px', 
                                                    fontSize: '10px', 
                                                    color: '#0F172A', 
                                                    fontWeight: '800', 
                                                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)',
                                                    transition: 'all 0.3s'
                                                }}>
                                                    View Detailed Report <ChevronRight size={14} color="#2563EB"/>
                                                </span>
                                            </div>
                                        )}

                                    </div>
                                );
                            })()}
                            
                        </div>
                    </div>

                    {/* 🚀 NAYA BOX YAHAN SE SHURU HOTA HAI (24px Gapping ke sath) */}
                    <div className="premium-glass animate-in" style={{ marginTop: '24px', padding: 0, overflow: 'hidden', background: 'rgba(255, 255, 255, 0.45)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.6)', boxShadow: '0 12px 35px rgba(0,0,0,0.03)' }}>
                          
                          {/* 🌟 Header matching WorkerHome "My Assigned Looms" 🌟 */}
                          <div style={{ padding: '20px', background: '#FFFFFF', borderBottom: '1px solid rgba(226, 232, 240, 0.8)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{display:'flex', flexDirection: 'column'}}>
                                  <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                      <div style={{ background: '#1E293B', padding: '6px', borderRadius: '10px'  }}>
                                          <Factory size={16} color="#FFFFFF"/>
                                      </div>
                                      <span style={{fontSize:'14px', fontWeight:'800', color:'#0F172A'}}>Live Loom Status</span>
                                  </div>
                                  <div style={{ marginTop: '0px', paddingLeft: '32px' }}>
    <span style={{ fontSize: '8.5px', color: '#475569', fontWeight: '700', background: '#F8FAFC', padding: '4px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <Calendar size={10} color="#64748B" /> 
        {activeDateStr.split(/-TO-| TO /).length === 2 ? (
            <>
                {activeDateStr.split(/-TO-| TO /)[0]}
                <span style={{ opacity: 0.35, margin: '0 5px', fontWeight: '500' }}>TO</span>
                {activeDateStr.split(/-TO-| TO /)[1]}
            </>
        ) : activeDateStr}
    </span>
</div>
                              </div>

                              {/* 🌟 Right Side: Active Looms Badge '0 / 10 Active' style from WorkerHome 🌟 */}
                              <div style={{ background: '#FFFFFF', borderRadius: '14px', padding: '6px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10B981', boxShadow: '0 0 10px rgba(16,185,129,0.6)' }} />
                                  <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase' }}>
                                      <span style={{ color: '#0F172A', fontSize: '14px', fontWeight: '900' }}>{Number(looms.filter(loom => isLoomActive(loom?.last_updated)).length)}</span> / {Number(looms.length)} Active
                                  </span>
                              </div>
                          </div>

                          {/* 🌟 LOOMS GRID (Solid White Background inside curved box) 🌟 */}
                          <div style={{ padding: '20px', background: 'rgba(255, 255, 255, 0.81)' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', width: '100%' }}>
                                  {looms.length === 0 ? (
                                      /* 100x Real Skeleton Cards */
                                      Array.from({ length: 8 }).map((_, i) => (
                                          <div key={`skel-${i}`} className="skeleton-box" style={{ height: '90px', width: '100%', backdropFilter: 'blur(10px)' }}></div>
                                      ))
                                  ) : (
                                      looms.map(loom => {
                                          const isActive = isLoomActive(loom.last_updated);
                                          const baseMeters = parseFloat(loom.meters || 0);
                                          
                                          let history = { week: 0, week_day: 0, week_night: 0, month: 0, month_day: 0, month_night: 0, total: 0, total_day: 0, total_night: 0 };
                                          if (Array.isArray(loomHistory)) {
                                              const found = loomHistory.find(h => String(h.loom_id).replace(/[^0-9]/g, '') === String(loom.loom_id));
                                              if (found) history = {...history, ...found};
                                          } else if (typeof loomHistory === 'object' && loomHistory !== null) {
                                              const foundObj = loomHistory[loom.loom_id] || loomHistory[String(loom.loom_id)] || loomHistory[String(loom.loom_id).padStart(2, '0')];
                                              if (foundObj) history = {...history, ...foundObj};
                                          }

                                          const hw = parseFloat(history.week || 0); const hwd = parseFloat(history.week_day || 0); const hwn = parseFloat(history.week_night || 0);
                                          const hm = parseFloat(history.month || 0); const hmd = parseFloat(history.month_day || 0); const hmn = parseFloat(history.month_night || 0);
                                          const ht = parseFloat(history.total || 0); const htd = parseFloat(history.total_day || 0); const htn = parseFloat(history.total_night || 0);

                                          let todayDay = loom.day_meters > 0 ? loom.day_meters : (activeShift === 'day' ? baseMeters : 0);
                                          let todayNight = loom.night_meters > 0 ? loom.night_meters : (activeShift === 'night' ? baseMeters : 0);

                                          let filterTotal = baseMeters; 
                                          let shiftDay = todayDay;
                                          let shiftNight = todayNight;

                                          if (loomTimeFilter === 'week') { filterTotal = hw + baseMeters; shiftDay = hwd + todayDay; shiftNight = hwn + todayNight; }
                                          if (loomTimeFilter === 'month') { filterTotal = hm + baseMeters; shiftDay = hmd + todayDay; shiftNight = hmn + todayNight; }
                                          if (loomTimeFilter === 'total') { filterTotal = ht + baseMeters; shiftDay = htd + todayDay; shiftNight = htn + todayNight; }

                                          let timeLabel = loomTimeFilter === 'today' ? ' आज का' : loomTimeFilter === 'week' ? ' इस हफ्ते का' : loomTimeFilter === 'month' ? ' इस महीने का' : ' अब तक का';
                                          
                                          let loomBoxVoiceMsg = "";
                                          
                                          if (filterTotal === 0) {
                                              loomBoxVoiceMsg = ` लूम नंबर ${loom.loom_id} ${isActive ? ' अभी चालू है' : ' अभी बंद है'}, इस पर ${timeLabel} प्रोडक्शन जीरो मीटर हुआ है।`;
                                          } else {
                                              let voiceProd = parseFloat(filterTotal || 0).toFixed(1).replace('.', ' point ');
                                              loomBoxVoiceMsg = ` लूम नंबर ${loom.loom_id} ${isActive ? ' अभी चालू है' : ' अभी बंद है'}, इस पर ${timeLabel} टोटल प्रोडक्शन ${voiceProd} मीटर हुआ है, जिसमें `;
                                              if (activeShift === 'day') {
                                                  loomBoxVoiceMsg += ` नाईट की शिफ्ट में ${shiftNight.toFixed(1).replace('.', ' पॉइंट ')} मीटर प्रोडक्शन हो चुका है और दिन की शिफ्ट में ${shiftDay.toFixed(1).replace('.', ' पॉइंट ')} मीटर production चल रहा है।`;
                                              } else {
                                                  loomBoxVoiceMsg += ` दिन की शिफ्ट में ${shiftDay.toFixed(1).replace('.', ' पॉइंट ')} मीटर प्रोडक्शन हो चुका है और नाईट की शिफ्ट में ${shiftNight.toFixed(1).replace('.', ' पॉइंट ')} मीटर production चल रहा है।`;
                                              }
                                          }

                                         return (
                                              <div key={loom.loom_id} 
                                                  onClick={(e) => {
                                                      triggerHaptic([50, 50]); 
                                                      const rect = e.currentTarget.getBoundingClientRect(); 
                                                      handleVoiceClick(e, loomBoxVoiceMsg, () => {
                                                          setSelectedLoomPopup({
                                                              id: loom.loom_id,
                                                              isActive: isActive,
                                                              loom: loom,
                                                              loomHistory: loomHistory,
                                                              exactActiveDays: exactActiveDays,
                                                              todayStats: todayStats,
                                                              todayStr: todayStr,
                                                              weekStr: weekStr,
                                                              monthStr: monthStr,
                                                              totalStr: totalStr,
                                                              initialFilter: loomTimeFilter
                                                          });
                                                      });
                                                  }} 
                                                  style={{ 
                                                      background: '#ffffff', 
                                                      borderRadius: '18px', 
                                                      padding: '14px 10px', 
                                                      boxShadow: '0 6px 20px rgba(15,23,42,0.04)', 
                                                      border: '1px solid #E2E8F0', 
                                                      display: 'flex', 
                                                      flexDirection: 'column', 
                                                      minWidth: '0', 
                                                      cursor:'pointer',
                                                      transition: 'transform 0.2s', 
                                                      position: 'relative', 
                                                      overflow: 'hidden'
                                                  }}>                        
                                                  
                                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '0 6px' }}>
                                                      <h3 style={{ fontSize: '10px', fontWeight: '700', color: '#8e8e8eff', margin: 0 }}>LOOM {String(loom.loom_id).padStart(2, '0')}</h3>
                                                      {isActive ? (
                                                          <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10B981', boxShadow: '0 0 6px rgba(16, 185, 129, 0.8)' }}></span>
                                                          </div>
                                                      ) : (
                                                          <span style={{ fontSize: '8px', fontWeight: '600', color: '#000000ff', background: '#f1f5f9', padding: '2px 4px', borderRadius: '6px', letterSpacing: '0.5px' }}>OFF</span>
                                                      )}
                                                  </div>
                                                 
                                                 {filterTotal > 0 ? (
                                                      <>
                                                          <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                                                              <p style={{ fontSize: '14px', fontWeight: '800', color: '#000000ff', margin: 0, lineHeight: '1' }}>{filterTotal.toFixed(1)}</p>
                                                              <p style={{ fontSize: '8px', fontWeight: '600', color: '#64748B', marginTop: '3px',  letterSpacing: '0.5px' }}>Total Meter</p>
                                                          </div>
                                                         <div style={{ display: 'flex', gap: '4px', padding: '0 2px' }}>
                                                              
                                                              {/* 🌟 Day Shift Box 🌟 */}
                                                              <div style={{ 
                                                                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
                                                                  padding: '6px 2px', borderRadius: '8px', position: 'relative', overflow: 'hidden',
                                                                  background: activeShift === 'day' ? '#ffffff' : '#f8fafc',
                                                                  border: activeShift === 'day' 
                                                                            ? (isActive ? '1.5px solid transparent' : '1px solid #10B981') 
                                                                            : '1px solid #e2e8f0',
                                                                  opacity: activeShift === 'day' ? 1 : 0.45,
                                                                  transition: 'all 0.3s ease', zIndex: 1
                                                              }}>
                                                                  {/* 🔥 Continuous Animated Border (ONLY ON WHEN LOOM IS ACTIVE) */}
                                                                  {(isActive && activeShift === 'day') && (
                                                                      <div style={{
                                                                          position: 'absolute', top: '-50%', left: '-50%', width: '200%', height: '200%',
                                                                          background: 'conic-gradient(from 0deg, transparent 60%, #10B981 100%)',
                                                                          animation: 'spinGPU 3s linear infinite', zIndex: -2, pointerEvents: 'none'
                                                                      }}></div>
                                                                  )}
                                                                  {(isActive && activeShift === 'day') && (
                                                                      <div style={{
                                                                          position: 'absolute', inset: '1px', background: '#ffffff', borderRadius: '6px', zIndex: -1
                                                                      }}></div>
                                                                  )}
                                                                  <div style={{ fontSize: '7px', fontWeight: '800', marginBottom: '2px', letterSpacing:'0.5px', color: activeShift === 'day' ? '#10B981' : '#94A3B8' }}>DAY</div>
                                                                  <div style={{ fontSize: '11px', fontWeight: '800', color: activeShift === 'day' ? '#0F172A' : '#64748B' }}>{shiftDay.toFixed(0)}</div>
                                                              </div>
                                                              
                                                              {/* 🌟 Night Shift Box 🌟 */}
                                                              <div style={{ 
                                                                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
                                                                  padding: '6px 2px', borderRadius: '8px', position: 'relative', overflow: 'hidden',
                                                                  background: activeShift === 'night' ? '#ffffff' : '#f8fafc',
                                                                  border: activeShift === 'night' 
                                                                            ? (isActive ? '1.5px solid transparent' : '1px solid #10B981') 
                                                                            : '1px solid #e2e8f0',
                                                                  opacity: activeShift === 'night' ? 1 : 0.45,
                                                                  transition: 'all 0.3s ease', zIndex: 1
                                                              }}>
                                                                  {/* 🔥 Continuous Animated Border (ONLY ON WHEN LOOM IS ACTIVE) */}
                                                                  {(isActive && activeShift === 'night') && (
                                                                      <div style={{
                                                                          position: 'absolute', top: '-50%', left: '-50%', width: '200%', height: '200%',
                                                                          background: 'conic-gradient(from 0deg, transparent 60%, #10B981 100%)',
                                                                          animation: 'spinGPU 3s linear infinite', zIndex: -2, pointerEvents: 'none'
                                                                      }}></div>
                                                                  )}
                                                                  {(isActive && activeShift === 'night') && (
                                                                      <div style={{
                                                                          position: 'absolute', inset: '1px', background: '#ffffff', borderRadius: '6px', zIndex: -1
                                                                      }}></div>
                                                                  )}
                                                                  <div style={{ fontSize: '7px', fontWeight: '800', marginBottom: '2px', letterSpacing:'0.5px', color: activeShift === 'night' ? '#10B981' : '#94A3B8' }}>NIGHT</div>
                                                                  <div style={{ fontSize: '11px', fontWeight: '800', color: activeShift === 'night' ? '#0F172A' : '#64748B' }}>{shiftNight.toFixed(0)}</div>
                                                              </div>
                                                          </div>
                                                      </>
                                                  ) : (
                                                      <div style={{ 
                                                          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', 
                                                          background: '#F8FAFC', border: '1.5px dashed #cbd5e1', borderRadius: '12px', 
                                                          padding: '24px 8px', marginTop: '4px' 
                                                      }}>
                                                          <span style={{ fontSize: '10px', fontWeight: '800', color: '#94A3B8', opacity: 0.6, letterSpacing: '0.5px' }}>NO PRODUCTION</span>
                                                      </div>
                                                  )}
                                              </div>
                                          
                                          );
                                      })
                                  )}
                              </div>
                          </div>
                      </div>
                </div>
            </div>

                       
            {/* NAYA CODE: Yahan popup render ho raha hai */}
            {selectedLoomPopup && <LoomPopupModal data={selectedLoomPopup} appBg={appBg} activeShift={activeShift} onClose={() => setSelectedLoomPopup(null)} />}
        </>
    );
};

// --- COMPONENT: STATS MODAL (Full Screen Glassmorphism) ---
const StatsModal = ({ title, data, period, onClose, appBg, handleVoiceClick, shiftTimings }) => {
  const [selectedYearForMonths, setSelectedYearForMonths] = useState(null); 
  const [selectedMonthForDays, setSelectedMonthForDays] = useState(null);
  const [selectedWeekForDays, setSelectedWeekForDays] = useState(null); 
  const [selectedDayForLooms, setSelectedDayForLooms] = useState(null); 
  const ALL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const [currentMonthTab, setCurrentMonthTab] = useState(ALL_MONTHS[new Date().getMonth()]); 

  // Auto-scroll refs
  const currentRowRef = React.useRef(null);
  const activeMonthTabRef = React.useRef(null);

  useEffect(() => {
      if (currentRowRef.current) {
          currentRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
  }, [period, selectedYearForMonths, selectedMonthForDays, selectedWeekForDays, currentMonthTab]);

  useEffect(() => {
      if (activeMonthTabRef.current) {
          activeMonthTabRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center' });
      }
  }, [currentMonthTab]);

  const reportData = useMemo(() => {
    const resultRows = [];
    const todayObj = new Date();
    const currentYearStr = todayObj.getFullYear().toString();
    const currentMonthStr = ALL_MONTHS[todayObj.getMonth()];
    
    const offset = todayObj.getTimezoneOffset() * 60000;
    const currentDateStr = new Date(todayObj.getTime() - offset).toISOString().split('T')[0];

    const dailyWise = data.reports?.daily_wise || {};

    if (period === 'week') {
        const weeklyData = data.weekly || {};
        const startOfWeek = new Date(todayObj);
        const diffToSat = (todayObj.getDay() + 1) % 7; 
        startOfWeek.setDate(todayObj.getDate() - diffToSat);
        
        const datesToMap = [];
        for (let i=0; i<7; i++) {
            const d = new Date(startOfWeek);
            d.setDate(startOfWeek.getDate() + i);
            if (d > todayObj) break;
            const localOffset = d.getTimezoneOffset() * 60000;
            datesToMap.push(new Date(d.getTime() - localOffset).toISOString().split('T')[0]);
        }
        
        datesToMap.forEach(dateStr => {
            const r = weeklyData[dateStr] || { Day: 0, Night: 0, Total: 0, looms: {} }; 
            const d = new Date(dateStr);
            resultRows.push({ 
                rawDate: dateStr,
                label: `${d.getDate()} ${d.toLocaleDateString('en-US', {month:'long'})}`,
                subLabel: d.toLocaleDateString('en-US', {weekday:'long'}), 
                day: r.Day, night: r.Night, total: r.Total, looms: r.looms || {},
                isCurrent: dateStr === currentDateStr
            });
        });
    } else if (period === 'month') {
        const monthData = dailyWise[currentYearStr]?.[currentMonthTab] || {};
        const monthIndex = ALL_MONTHS.indexOf(currentMonthTab);
        const isCurrentMonth = currentMonthTab === currentMonthStr;
        const daysInMonth = new Date(currentYearStr, monthIndex + 1, 0).getDate();
        
        const datesToMap = [];
        for (let i=1; i<=daysInMonth; i++) {
            const d = new Date(currentYearStr, monthIndex, i);
            if (isCurrentMonth && d > todayObj) break;
            const localOffset = d.getTimezoneOffset() * 60000;
            datesToMap.push(new Date(d.getTime() - localOffset).toISOString().split('T')[0]);
        }
        
        datesToMap.forEach(dateStr => {
            const r = monthData[dateStr] || { Day: 0, Night: 0, Total: 0, looms: {} }; 
            const d = new Date(dateStr);
            resultRows.push({ 
                rawDate: dateStr,
                label: `${d.getDate()} ${d.toLocaleDateString('en-US', {month:'long'})}`,
                subLabel: d.toLocaleDateString('en-US', {weekday:'long'}), 
                day: r.Day, night: r.Night, total: r.Total, looms: r.looms || {},
                isCurrent: dateStr === currentDateStr
            });
        });
    } else if (period === 'total') {
        const reports = data.reports || { month_wise: {}, year_wise: {}, daily_wise: {} };

        if (selectedYearForMonths && selectedMonthForDays && selectedWeekForDays) {
            const wStart = new Date(selectedWeekForDays);
            for(let i=0; i<7; i++) {
                const d = new Date(wStart);
                d.setDate(wStart.getDate() + i);
                if (d > todayObj) break;

                const localOffset = d.getTimezoneOffset() * 60000;
                const dateStr = new Date(d.getTime() - localOffset).toISOString().split('T')[0];
                
                const yearStr = d.getFullYear().toString();
                const monthStr = ALL_MONTHS[d.getMonth()];
                
                const r = dailyWise[yearStr]?.[monthStr]?.[dateStr] || { Day: 0, Night: 0, Total: 0, looms: {} };
                resultRows.push({
                    rawDate: dateStr,
                    label: `${d.getDate()} ${d.toLocaleDateString('en-US', {month:'long'})}`,
                    subLabel: d.toLocaleDateString('en-US', {weekday:'long'}),
                    day: r.Day || 0,
                    night: r.Night || 0,
                    total: r.Total || 0,
                    looms: r.looms || {},
                    isCurrent: dateStr === currentDateStr
                });
            }
        } else if (selectedYearForMonths && selectedMonthForDays) {
            const monthData = dailyWise[selectedYearForMonths]?.[selectedMonthForDays] || {};
            const monthIndex = ALL_MONTHS.indexOf(selectedMonthForDays);
            const isCurrentMonth = selectedYearForMonths === currentYearStr && selectedMonthForDays === currentMonthStr;
            const daysInMonth = new Date(selectedYearForMonths, monthIndex + 1, 0).getDate();
            
            const weeksMap = new Map();

            for (let i = 1; i <= daysInMonth; i++) {
                const d = new Date(selectedYearForMonths, monthIndex, i);
                if (isCurrentMonth && d > todayObj) break;

                const diffToSat = (d.getDay() + 1) % 7; 
                const weekStart = new Date(d);
                weekStart.setDate(d.getDate() - diffToSat);
                weekStart.setHours(0,0,0,0);

                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekStart.getDate() + 6);
                weekEnd.setHours(23,59,59,999);

                const jan1 = new Date(selectedYearForMonths, 0, 1);
                const jan1Diff = (jan1.getDay() + 1) % 7;
                const firstSat = new Date(jan1);
                firstSat.setDate(jan1.getDate() - jan1Diff);
                firstSat.setHours(0,0,0,0);
                
                const weekNum = Math.floor((weekStart - firstSat) / (7 * 24 * 60 * 60 * 1000)) + 1;
                const weekKey = weekStart.getTime();

                if (!weeksMap.has(weekKey)) {
                    const formatD = (dateObj) => `${dateObj.getDate()} ${ALL_MONTHS[dateObj.getMonth()].substring(0,3)}`;
                    const isTransition = (weekStart.getMonth() === 11 && weekEnd.getMonth() === 0);
                    const finalLabel = isTransition ? "TRANSITION WEEK" : `WEEK ${weekNum}`;

                    weeksMap.set(weekKey, {
                        weekKey: weekKey,
                        weekLabel: finalLabel,
                        dateLabel: `${formatD(weekStart)} - ${formatD(weekEnd)}`,
                        day: 0, night: 0, total: 0, isCurrentWeek: false
                    });
                }

                const weekData = weeksMap.get(weekKey);
                
                const localOffset = d.getTimezoneOffset() * 60000;
                const dateStr = new Date(d.getTime() - localOffset).toISOString().split('T')[0];

                if (dateStr === currentDateStr) {
                    weekData.isCurrentWeek = true;
                }

                const r = monthData[dateStr];
                if (r) {
                    weekData.day += r.Day || 0;
                    weekData.night += r.Night || 0;
                    weekData.total += r.Total || 0;
                }
            }

            Array.from(weeksMap.values()).forEach(w => {
                resultRows.push({
                    isWeekRow: true, 
                    weekVal: w.weekKey,
                    label: w.weekLabel,
                    subLabel: w.dateLabel,
                    day: w.day,
                    night: w.night,
                    total: w.total,
                    isCurrent: w.isCurrentWeek
                });
            });
        } else if (selectedYearForMonths) {
            const yearMonths = reports.month_wise[selectedYearForMonths] || {};
            const availableMonths = [];
            for(let i=0; i<12; i++) {
                if (selectedYearForMonths === currentYearStr && i > todayObj.getMonth()) break;
                availableMonths.push(ALL_MONTHS[i]);
            }
            availableMonths.forEach(monthName => {
                const r = yearMonths[monthName] || { Day: 0, Night: 0, Total: 0 };
                resultRows.push({ 
                    isMonthRow: true, monthVal: monthName, label: monthName, subLabel: selectedYearForMonths, 
                    day: r.Day, night: r.Night, total: r.Total,
                    isCurrent: selectedYearForMonths === currentYearStr && monthName === currentMonthStr
                });
            });
        } else {
            const availableYears = Object.keys(reports.year_wise).sort((a,b) => a-b);
            if (!availableYears.includes(currentYearStr)) availableYears.push(currentYearStr);
            
            availableYears.forEach(yearName => {
                const r = reports.year_wise[yearName] || { Day: 0, Night: 0, Total: 0 };
                resultRows.push({ 
                    isYearRow: true, yearVal: yearName, label: yearName, subLabel: "Yearly Record", 
                    day: r.Day, night: r.Night, total: r.Total,
                    isCurrent: yearName === currentYearStr
                });
            });
        }
    }
    return resultRows;
  }, [period, data, selectedYearForMonths, selectedMonthForDays, selectedWeekForDays, currentMonthTab]);

  const handleBack = () => { 
      window.speechSynthesis.cancel();
      if (selectedDayForLooms) setSelectedDayForLooms(null); 
      else if (selectedWeekForDays) setSelectedWeekForDays(null); 
      else if (selectedMonthForDays) setSelectedMonthForDays(null); 
      else if (selectedYearForMonths) setSelectedYearForMonths(null); 
  };
  const isBackVisible = selectedYearForMonths !== null || selectedDayForLooms !== null;

  const shiftStart = shiftTimings?.day?.split(' - ')?.[0] || "08:00 AM";
  const shiftEnd = shiftTimings?.day?.split(' - ')?.[1] || "08:00 PM";

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: appBg, zIndex: 9999, display: 'flex', flexDirection: 'column' }}>
        
        {/* 🌟 MAIN UPDATED MASTER HEADER WITH BACK BUTTON & SUBTITLE STRIP 🌟 */}
        <div style={{ background: '#ffffff', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', position:'sticky', top:0, zIndex:10, width: '100%', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                
                <button onClick={(e) => handleVoiceClick(e, "रिपोर्ट से बैक जा रहे हैं।", isBackVisible ? handleBack : onClose)} style={{ background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', cursor: 'pointer', padding: '6px', display:'flex', alignItems:'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    <ArrowLeft size={18} color="#0F172A" strokeWidth={2.5}/>
                </button>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <h1 style={{ fontSize: '15px', fontWeight: '900', color: '#0F172A', margin: 0, letterSpacing:'-0.3px', textTransform: 'uppercase' }}>
                        {selectedDayForLooms ? `${selectedDayForLooms.label}`
                        : selectedWeekForDays ? `DAILY DATA`
                        : selectedMonthForDays ? `${selectedMonthForDays} ${selectedYearForMonths}` 
                        : selectedYearForMonths ? `Monthly Data (${selectedYearForMonths})` 
                        : title}
                    </h1>
                    
                    {/* 🌟 WORKERHOME STYLE DATE STRIP EMBEDDED DIRECTLY IN HEADER 🌟 */}
                    {selectedDayForLooms ? (
                        <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase' }}>
                            {selectedDayForLooms.subLabel}
                        </span>
                    ) : !isBackVisible && (() => {
                        const todayB = new Date();
                        const sWeek = new Date(todayB); sWeek.setDate(todayB.getDate() - ((todayB.getDay() + 1) % 7));
                        const eWeek = new Date(sWeek); eWeek.setDate(sWeek.getDate() + 6);
                        
                        const sMonth = new Date(todayB.getFullYear(), todayB.getMonth(), 1);
                        const eMonth = new Date(todayB.getFullYear(), todayB.getMonth() + 1, 0);

                        let oDate = "1 JANUARY 2026";
                        if (data?.reports?.daily_wise) {
                            let dArr = [];
                            Object.values(data.reports.daily_wise).forEach(y => Object.values(y).forEach(m => Object.keys(m).forEach(d => dArr.push(d))));
                            if (dArr.length > 0) { dArr.sort(); oDate = new Date(dArr[0]).toLocaleDateString('en-IN', {day:'numeric', month:'long', year:'numeric'}); }
                        }

                        let prefixText = "TOTAL";
                        let dateStartText = oDate;
                        let dateEndText = "Till Now";

                        if (period === 'week') {
                            prefixText = "THIS WEEK";
                            dateStartText = sWeek.toLocaleDateString('en-IN', {day:'numeric', month:'long', year:'numeric'});
                            dateEndText = eWeek.toLocaleDateString('en-IN', {day:'numeric', month:'long', year:'numeric'});
                        } else if (period === 'month') {
                            prefixText = "THIS MONTH";
                            dateStartText = sMonth.toLocaleDateString('en-IN', {day:'numeric', month:'long', year:'numeric'});
                            dateEndText = eMonth.toLocaleDateString('en-IN', {day:'numeric', month:'long', year:'numeric'});
                        }

                        return (
                            <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '800', letterSpacing: '0.5px', textTransform: 'uppercase', margin: 0 }}>
                                <span style={{ fontSize: '9px', color: '#94A3B8', opacity: 0.7 }}>{prefixText}</span>
                                <span style={{ fontSize: '9px', color: '#64748B' }}> • {dateStartText} </span>
                                <span style={{ fontSize: '9px', color: '#94A3B8', opacity: 0.5, margin: '0 4px', display: 'inline-block' }}>TO</span>
                                <span style={{ fontSize: '9px', color: '#64748B' }}> {dateEndText}</span>
                            </span>
                        );
                    })()}
                </div>
            </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          
           {/* 🌟 SOLID WHITE TABLE CONTAINER 🌟 */}
               <div className="animate-in" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', boxShadow: '0 12px 35px rgba(0,0,0,0.05)', overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 20px)' }}>
               
               {selectedDayForLooms ? (
                    <div style={{ padding: '16px', overflowY: 'auto' }}>
                        {/* Duty and Rate Strips */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                            <div style={{ flex: 1, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Clock size={14} color="#64748B" />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <span style={{ fontSize: '7px', fontWeight: '800', color: '#94A3B8', letterSpacing: '0.5px' }}>DUTY TIME</span>
                                    <span style={{ fontSize: '10px', fontWeight: '800', color: '#0F172A', display: 'flex', alignItems: 'center' }}>
                                        {shiftStart} <span style={{ color: '#94A3B8', fontSize: '8px', margin: '0 2px' }}>TO</span> {shiftEnd}
                                        {(() => {
                                            const s = parseTimeStr(shiftStart);
                                            const e = parseTimeStr(shiftEnd);
                                            if(!s || !e) return null;
                                            let diff = e - s;
                                            if (diff < 0) diff += 1440;
                                            const h = Math.floor(diff / 60);
                                            const m = diff % 60;
                                            const dur = m > 0 ? `${h}:${String(m).padStart(2,'0')} HOURS` : `${h} HOURS`;
                                            return <><span style={{ color: '#94A3B8', margin: '0 4px', fontSize: '10px' }}>•</span><span style={{ color: '#64748B', fontSize: '10px', fontWeight: '800' }}>{dur}</span></>;
                                        })()}
                                    </span>
                                </div>
                            </div>
                            <div style={{ flex: 1, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <IndianRupee size={14} color="#64748B" />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <span style={{ fontSize: '7px', fontWeight: '800', color: '#94A3B8', letterSpacing: '0.5px' }}>RATE / METER</span>
                                    <span style={{ fontSize: '10px', fontWeight: '800', color: '#0F172A' }}>
                                        VARIES
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Looms Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
                            {Object.entries(selectedDayForLooms.looms).map(([lId, meters]) => {
                                if(parseFloat(meters) <= 0) return null;
                                const loomIdLabel = String(lId).padStart(2, '0');
                                return (
                                    <div key={lId} style={{ background: '#FFFFFF', borderRadius: '16px', padding: '10px 6px', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03)', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '8px', alignItems: 'center', padding: '0 4px' }}>
                                            <span style={{ fontSize: '9px', fontWeight: '900', color: '#94A3B8', letterSpacing: '0.2px' }}>LOOM {loomIdLabel}</span>
                                        </div>
                                        <div style={{ width: '100%', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <span style={{ fontSize: '6px', fontWeight: '800', color: '#64748B', letterSpacing: '0.5px', marginBottom: '4px' }}>TOTAL</span>
                                            <span style={{ fontSize: '13px', fontWeight: '900', color: '#0F172A' }}>{parseFloat(meters).toFixed(1)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Total Production Box */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '12px 14px', marginTop: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ background: '#E0F2FE', padding: '6px', borderRadius: '8px' }}>
                                    <Layers size={15} color="#0284C7" />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    <span style={{ fontSize: '8px', fontWeight: '800', color: '#94A3B8', letterSpacing: '0.5px' }}>TOTAL PRODUCTION</span>
                                    <span style={{ fontSize: '13px', fontWeight: '900', color: '#0F172A' }}>
                                        {parseFloat(selectedDayForLooms.total || 0).toFixed(1)} <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '700' }}>METER</span>
                                    </span>
                                </div>
                            </div>
                            <div style={{ height: '30px', width: '1px', background: '#E2E8F0' }}></div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'right' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-end' }}>
                                    <span style={{ fontSize: '8px', fontWeight: '800', color: '#059669', letterSpacing: '0.5px' }}>TOTAL EARNING</span>
                                    <span style={{ fontSize: '13px', fontWeight: '900', color: '#10B981' }}>
                                        MULTIPLE RATES
                                    </span>
                                </div>
                                <div style={{ background: '#D1FAE5', padding: '6px', borderRadius: '8px' }}>
                                    <IndianRupee size={15} color="#059669" />
                                </div>
                            </div>
                        </div>

                    </div>
                ) : (

                <div style={{ overflow: 'auto', flex: 1, minHeight: 0, padding: '0 16px 16px 16px' }}>
                    <table style={{width:'100%', borderCollapse:'collapse', fontSize:'11px', whiteSpace: 'nowrap'}}>
                        <thead style={{position: 'sticky', top: 0, background: '#ffffff', zIndex: 1}}>
                        <tr style={{textAlign:'left', color:'#64748B', textTransform:'uppercase', letterSpacing:'0.8px'}}>
                            <th style={{padding:'16px 8px', borderBottom:'1px solid #e2e8f0', fontSize:'11px', fontWeight:'800'}}>Timeline</th>
                            <th style={{padding:'16px 8px', borderBottom:'1px solid #e2e8f0', fontSize:'10px', fontWeight:'800', background: '#F8FAFC'}}>Day</th>
                            <th style={{padding:'16px 8px', borderBottom:'1px solid #e2e8f0', fontSize:'10px', fontWeight:'800', background: '#F1F5F9'}}>Night</th>
                            <th style={{padding:'16px 8px', textAlign:'right', borderBottom:'1px solid #e2e8f0', fontSize:'10px', fontWeight:'800'}}>Total</th>
                        </tr>
                        </thead>
                        <tbody>
                        {reportData.length > 0 ? reportData.map((row, idx) => {
                            const hasProduction = parseFloat(row.total || 0) > 0;
                            
                            let rowVoiceMsg = null;
                            if (row.isYearRow) {
                                if (hasProduction) {
                                    rowVoiceMsg = `आपके टेक्सटाइल कारखाने में साल ${row.yearVal} का टोटल ${parseFloat(row.total).toFixed(1)} मीटर प्रोडक्शन किया गया है, जिसमें दिन की शिफ्ट में ${parseFloat(row.day).toFixed(1)} मीटर प्रोडक्शन हुआ है और रात की शिफ्ट में ${parseFloat(row.night).toFixed(1)} मीटर प्रोडक्शन हुआ है।`;
                                } else {
                                    rowVoiceMsg = `साल ${row.yearVal} में आपके कारखाने में कोई प्रोडक्शन नहीं हुआ है।`;
                                }
                            } else if (row.isMonthRow) {
                                if (hasProduction) {
                                    rowVoiceMsg = `आपके टेक्सटाइल कारखाने में ${row.monthVal} के महीने का टोटल ${parseFloat(row.total).toFixed(1)} मीटर प्रोडक्शन किया गया है, जिसमें दिन की शिफ्ट में ${parseFloat(row.day).toFixed(1)} मीटर प्रोडक्शन हुआ है और रात की शिफ्ट में ${parseFloat(row.night).toFixed(1)} मीटर प्रोडक्शन हुआ है।`;
                                } else {
                                    rowVoiceMsg = `${row.monthVal} महीने में आपके कारखाने में कोई प्रोडक्शन नहीं हुआ है।`;
                                }
                            } else if (row.isWeekRow) {
                                if (hasProduction) {
                                    rowVoiceMsg = `${row.label} में आपके कारखाने का टोटल ${parseFloat(row.total).toFixed(1)} मीटर प्रोडक्शन किया गया है।`;
                                }
                            } else {
                                const dayNameMap = { 'Sunday': ' इतवार', 'Monday': ' पीर', 'Tuesday': ' मंगल', 'Wednesday': ' बुध', 'Thursday': ' जुमेरात', 'Friday': ' जुम्मा', 'Saturday': ' शनिवार' };
                                const hindiDay = dayNameMap[row.subLabel] || row.subLabel;
                                if (hasProduction) {
                                    rowVoiceMsg = `आपके टेक्सटाइल कारखाने में ${row.label} को ${hindiDay} के दिन टोटल ${parseFloat(row.total).toFixed(1)} मीटर प्रोडक्शन किया है, जिसमें दिन की शिफ्ट में ${parseFloat(row.day).toFixed(1)} मीटर प्रोडक्शन हुआ है और रात की शिफ्ट में ${parseFloat(row.night).toFixed(1)} मीटर प्रोडक्शन हुआ है।`;
                                } else {
                                    rowVoiceMsg = `${row.label} को ${hindiDay} के दिन आपके कारखाने में कोई प्रोडक्शन नहीं हुआ है।`;
                                }
                            }
                            
                            return (
                            <tr key={idx} 
                                ref={row.isCurrent ? currentRowRef : null} 
                                onClick={(e) => {
                                    if (rowVoiceMsg) {
                                        window.speechSynthesis.cancel(); 
                                        handleVoiceClick(e, rowVoiceMsg, null);
                                    }
                                }}
                                style={{
                                borderBottom:'1px solid #f1f5f9',
                                borderLeft: row.isCurrent ? '3px solid #10B981' : '3px solid transparent',
                                background: 'transparent',
                                cursor: rowVoiceMsg ? 'pointer' : 'default' 
                            }}>
                              <td style={{padding:'14px 8px', paddingLeft: row.isCurrent ? '12px' : '8px'}}>
                                    <div style={{fontWeight:'700', color: row.isCurrent ? '#10B981' : '#0F172A', fontSize:'12px'}}>
                                        {row.label}
                                        {row.isCurrent && <span style={{fontSize:'8px', marginLeft:'6px', background:'#10B981', color:' white', padding:'2px 4px', borderRadius:'4px', verticalAlign:'middle', letterSpacing:'0.5px'}}>CURRENT</span>}
                                    </div>
                                    {row.isYearRow ? (
                                        <div onClick={(e) => { 
                                            e.stopPropagation(); window.speechSynthesis.cancel(); setSelectedYearForMonths(row.yearVal); 
                                        }} style={{fontSize:'9px', color:'#0F172A', cursor:'pointer', fontWeight:'700', marginTop:'6px', display:'inline-flex', alignItems:'center', gap:'4px', background:'#f8fafc', border: '1px solid #e2e8f0', padding:'6px 10px', borderRadius:'10px'}}>View Monthly <ArrowRightLeft size={10} /></div>
                                    ) : row.isMonthRow ? (
                                        <div onClick={(e) => { 
                                            e.stopPropagation(); window.speechSynthesis.cancel(); setSelectedMonthForDays(row.monthVal); 
                                        }} style={{fontSize:'9px', color:'#0F172A', cursor:'pointer', fontWeight:'700', marginTop:'6px', display:'inline-flex', alignItems:'center', gap:'4px', background:'#f8fafc', border: '1px solid #e2e8f0', padding:'6px 10px', borderRadius:'10px'}}>View Weekly <ArrowRightLeft size={10} /></div>
                                    ) : row.isWeekRow ? (
                                        <div onClick={(e) => { 
                                            e.stopPropagation(); window.speechSynthesis.cancel(); setSelectedWeekForDays(row.weekVal); 
                                        }} style={{fontSize:'9px', color:'#0F172A', cursor:'pointer', fontWeight:'700', marginTop:'6px', display:'inline-flex', alignItems:'center', gap:'4px', background:'#f8fafc', border: '1px solid #e2e8f0', padding:'6px 10px', borderRadius:'10px'}}>View Daily <ArrowRightLeft size={10} /></div>
                                    ) : (
                                        <>
                                            <div style={{fontSize:'9px', color:'#64748B', fontWeight:'600', marginTop:'4px'}}>{row.subLabel}</div>
                                        </>
                                    )}
                                </td>
                                
                               {hasProduction ? (
                                    <>
                                        <td style={{padding:'14px 8px', color:'#334155', fontWeight:'600', background: '#F8FAFC'}}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span>{parseFloat(row.day || 0).toFixed(1)}</span>
                                                <span style={{fontSize:'8px', fontWeight:'600', color:'#94A3B8', marginTop:'2px'}}>Meter</span>
                                            </div>
                                        </td>
                                        <td style={{padding:'14px 8px', color:'#334155', fontWeight:'600', background: '#F1F5F9'}}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span>{parseFloat(row.night || 0).toFixed(1)}</span>
                                                <span style={{fontSize:'8px', fontWeight:'600', color:'#94A3B8', marginTop:'2px'}}>Meter</span>
                                            </div>
                                        </td>
                                        <td style={{padding:'14px 8px', textAlign:'right', fontWeight:'800', color: '#0F172A', fontSize:'14px'}}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                <span>{parseFloat(row.total || 0).toFixed(1)}</span>
                                                <span style={{fontSize:'9px', color:'#64748B', fontWeight:'700', marginTop:'2px'}}>Meter</span>
                                                {!row.isYearRow && !row.isMonthRow && !row.isWeekRow && (
                                                    <div 
                                                        onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            if(window.speechSynthesis) window.speechSynthesis.cancel(); 
                                                            setSelectedDayForLooms(row); 
                                                        }} 
                                                        style={{fontSize:'8px', color:'#2563EB', cursor:'pointer', fontWeight:'800', marginTop:'6px', display:'inline-flex', alignItems:'center', gap:'2px', background:'#EFF6FF', border: '1px solid #BFDBFE', padding:'4px 8px', borderRadius:'6px', textTransform:'uppercase', letterSpacing:'0.5px'}}
                                                    >
                                                        VIEW <ChevronRight size={10} />
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </>
                                ) : (
                                    <td colSpan="3" style={{padding:'14px 8px', textAlign:'center', verticalAlign:'middle', background: '#F8FAFC'}}>
                                        <span style={{fontSize:'8px', fontWeight:'700', color:'#94A3B8'}}>
                                             NO PRODUCTION  
                                        </span>
                                    </td>
                                )}
                            </tr>
                            )}) : (
                                <tr><td colSpan="4" style={{padding:'40px', textAlign:'center', color:'#64748B', fontWeight:'600'}}>No Records Found</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                )}
            </div>
            <div style={{height: '30px'}}></div>
        </div>
    </div>
  );
};

// --- MAIN APP ---
function App() {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showLoomModal, setShowLoomModal] = useState(false);

  // ===== 🌟 PREMIUM SCROLL LOCK 🌟 =====
  useEffect(() => {
      if (showLoomModal || isProfileOpen) {
          document.body.style.overflow = 'hidden'; 
      } else {
          document.body.style.overflow = 'auto';   
      }
      return () => { document.body.style.overflow = 'auto'; };
  }, [showLoomModal, isProfileOpen]);

  const closeLoomModal = () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel(); 
      setShowLoomModal(false);
  };

  const [appActiveLooms, setAppActiveLooms] = useState([]); // 👈 बस यह एक लाइन यहाँ पेस्ट करनी है
  
  const [authUser, setAuthUser] = useState(null); 
  const [view, setView] = useState('dashboard');
  const [statsModalData, setStatsModalData] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
const [maxLooms, setMaxLooms] = useState(() => parseInt(localStorage.getItem('mp_maxLooms')) || 0); 
  const [loomHistory, setLoomHistory] = useState(() => JSON.parse(localStorage.getItem('mp_loomHistory')) || {});
  const [looms, setLooms] = useState(() => JSON.parse(localStorage.getItem('mp_looms')) || []); 
  const [workers, setWorkers] = useState(() => JSON.parse(localStorage.getItem('mp_workers')) || []);
  const [dbStats, setDbStats] = useState(() => JSON.parse(localStorage.getItem('mp_dbStats')) || { summaries: { week: {Day:0, Night:0, Total:0}, month: {Day:0, Night:0, Total:0}, total: {Day:0, Night:0, Total:0} }, weekly: {}, reports: { month_wise: {}, year_wise: {}, daily_wise: {} } });
  const [shiftTimings, setShiftTimings] = useState({ day: '08:00 am', night: '08:00 pm' });
  const [statsTab, setStatsTab] = useState('week');
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportParams, setReportParams] = useState({
      fromDate: '', toDate: '', workerId: 'all', loomId: 'all', shift: 'all'
  });

  const [appBg, setAppBg] = useState(() => localStorage.getItem('owner_appBg') || "url('/OHOME.jpg') center / cover no-repeat fixed");
  const [notifOn, setNotifOn] = useState(() => {
      const saved = localStorage.getItem('owner_notifOn');
      return saved !== null ? JSON.parse(saved) : true;
  });

  // 🚀 NEW: DEFINING MISSING STATES FOR PREMIUM THEME ENGINE
  const [appFont, setAppFont] = useState(() => localStorage.getItem('owner_appFont') || 'Plus Jakarta Sans');
  const [appScale, setAppScale] = useState(() => parseFloat(localStorage.getItem('owner_appScale')) || 1.0);
  const [appTextSize, setAppTextSize] = useState(() => parseInt(localStorage.getItem('owner_appTextSize')) || 12);

  // 🚀 NEW: LISTEN FOR OWNER SETTINGS CHANGES
  useEffect(() => {
      const handleThemeUpdate = () => {
          setAppFont(localStorage.getItem('owner_appFont') || 'Plus Jakarta Sans');
          setAppScale(parseFloat(localStorage.getItem('owner_appScale')) || 1.0);
          setAppTextSize(parseInt(localStorage.getItem('owner_appTextSize')) || 12);
          
          const savedBg = localStorage.getItem('owner_appBg');
          if (savedBg) setAppBg(savedBg);
      };
      window.addEventListener('ownerThemeChanged', handleThemeUpdate);
      return () => window.removeEventListener('ownerThemeChanged', handleThemeUpdate);
  }, []);

  const {
    voiceAssist, setVoiceAssist,
    showGuideModal, setShowGuideModal,
    activeGuideFlow, currentGuideMessage,
    toggleVoiceAssist, handleVoiceClick,
    handleFocusVoice, handleSelectVoice, handleTextBlurVoice,
    startSmartGuide, endSmartGuide, advanceNextStep,
    speakHinglish, setPendingConfirm
  } = useSmartGuide(setView);

  useEffect(() => { localStorage.setItem('owner_appBg', appBg); }, [appBg]);
  useEffect(() => { localStorage.setItem('owner_notifOn', JSON.stringify(notifOn)); }, [notifOn]);

  const isFetchingRef = useRef(false);

  const fetchProfileAndData = async () => {
      if(!authUser || isFetchingRef.current) return; 
      isFetchingRef.current = true;
      
      try { 
          await fetch(`${API_BASE}/auto_update_shift.php`).catch(() => {}); 
      } catch (e) {}
      
      try {
          const ts = Date.now();
          // .catch handling jisse network drop hone par Promise.all crash na ho
          const [lRes, wRes, sRes, pRes, lhRes] = await Promise.all([ 
              fetch(`${API_BASE}/get_production.php?t=${ts}`).catch(() => ({ ok: false })), 
              fetch(`${API_BASE}/get_worker_performance.php?t=${ts}`).catch(() => ({ ok: false })), 
              fetch(`${API_BASE}/get_stats.php?t=${ts}`).catch(() => ({ ok: false })), 
              fetch(`${API_BASE}/get_owner_profile.php?t=${ts}`).catch(() => ({ ok: false })), 
              fetch(`${API_BASE}/get_loom_stats.php?t=${ts}`).catch(() => ({ ok: false })) 
          ]);
          
          if (lhRes && lhRes.ok) { 
              const lhData = await lhRes.json().catch(() => null); 
              if (lhData && lhData.status === 'success') { 
                  setLoomHistory(lhData.data); 
                  localStorage.setItem('mp_loomHistory', JSON.stringify(lhData.data));
              } 
          }
          if (lRes && lRes.ok) { 
              const lData = await lRes.json().catch(() => null); 
              if (lData) {
                  const fetchedLooms = lData.data || lData || [];
                  setLooms(fetchedLooms); 
                  localStorage.setItem('mp_looms', JSON.stringify(fetchedLooms));
              }
          }
          if (wRes && wRes.ok) { 
              const wData = await wRes.json().catch(() => null);
              if (wData) {
                  setWorkers(wData); 
                  localStorage.setItem('mp_workers', JSON.stringify(wData));
              }
          }
          if (sRes && sRes.ok) { 
              const data = await sRes.json().catch(() => null); 
              if (data) {
                  const computedStats = { summaries: data.summaries || { week:{Day:0,Night:0,Total:0}, month:{Day:0,Night:0,Total:0}, total:{Day:0,Night:0,Total:0} }, weekly: data.weekly || {}, reports: data.reports || { month_wise: {}, year_wise: {}, daily_wise: {} } };
                  setDbStats(computedStats); 
                  localStorage.setItem('mp_dbStats', JSON.stringify(computedStats));
              }
          }
          if (pRes && pRes.ok) { 
              const prof = await pRes.json().catch(() => null); 
              if(prof && prof.status === 'success') { 
                  const ml = parseInt(prof.data.total_looms) || 0;
                  setMaxLooms(ml); 
                  localStorage.setItem('mp_maxLooms', ml.toString());
                  if (prof.data.shift_day_time) {
                      setShiftTimings({ day: prof.data.shift_day_time, night: prof.data.shift_night_time });
                  }
              } 
          }
      } catch (e) { 
          console.error("Data Load Error:", e); 
      } finally {
          isFetchingRef.current = false;
      }
  };

  useEffect(() => { 
      fetchProfileAndData(); 
      // Slow internet connectivity lines ko smooth rakhne ke liye polling time 10 second kiya gaya hai
      const dataTimer = setInterval(fetchProfileAndData, 10000); 
      return () => clearInterval(dataTimer); 
  }, [authUser]);

const mergedLooms = useMemo(() => {
      const allLooms = []; 
      const safeLooms = Array.isArray(looms) ? looms : (looms.data || []); 
      
      // 1. सबसे पहले एपीआई से आने वाले लाइव लूम्स की क्लीन आईडी का एरे बना लेते हैं
      const apiLoomIds = safeLooms.map(l => {
          const numStr = String(l.loom_id).replace(/[^0-9]/g, '');
          return numStr ? parseInt(numStr, 10) : null;
      }).filter(id => id !== null);

      // 2. यूनीक लाइव आईडी की लिस्ट निकालें
      const uniqueApiIds = [...new Set(apiLoomIds)].sort((a, b) => a - b);

      // 3. अगर maxLooms सेट है, तो 1 से maxLooms तक की लिस्ट लें, अन्यथा लाइव एपीआई की लिस्ट लें
      let loomNumbers = [];
      const totalLoomCount = parseInt(maxLooms, 10) || 0;

      if (totalLoomCount > 0) {
          loomNumbers = Array.from({ length: totalLoomCount }, (_, i) => i + 1);
      } else if (uniqueApiIds.length > 0) {
          loomNumbers = uniqueApiIds;
      } else if (appActiveLooms && appActiveLooms.length > 0) {
          loomNumbers = appActiveLooms.map(id => parseInt(String(id).replace(/[^0-9]/g, ''), 10)).filter(id => !isNaN(id));
      }

      // 4. अब शुद्ध नंबर्स के आधार पर डेटा मैप करें (यह '01' और '1' या 'L01' के सारे अंतर खत्म कर देगा)
      loomNumbers.forEach(l_id => {
          const currentLoomNum = parseInt(String(l_id), 10);

          const apiLoom = safeLooms.find(l => {
              const cleanApiId = parseInt(String(l.loom_id).replace(/[^0-9]/g, ''), 10);
              return cleanApiId === currentLoomNum;
          });

          allLooms.push({ 
              loom_id: currentLoomNum, // शुद्ध नंबर ताकि रेंडरिंग में गड़बड़ी न हो
              meters: apiLoom ? parseFloat(apiLoom.meters || 0) : 0, 
              day_meters: apiLoom ? parseFloat(apiLoom.day_meters || apiLoom.day || 0) : 0,
              night_meters: apiLoom ? parseFloat(apiLoom.night_meters || apiLoom.night || 0) : 0,
              last_updated: apiLoom ? apiLoom.last_updated : null, 
              worker_id: apiLoom ? (apiLoom.worker_id || apiLoom.worker_id_ref) : null, 
              worker_name: apiLoom ? (apiLoom.worker_name || apiLoom.worker || apiLoom.name) : null 
          });
      });

      return allLooms;
  }, [maxLooms, appActiveLooms, looms]);

  const handleLogout = () => { setAuthUser(null); setView('dashboard'); };

          // 🌟 NAYA LOGIC: Check all App.js Modals
  const isAnyModalOpen = showSearch || statsModalData || showGuideModal;

  // ===== 🌟 PREMIUM SCROLL LOCK (Hostinger / Mobile Fix) 🌟 =====
  useEffect(() => {
      if (isAnyModalOpen) {
          document.body.style.overflow = 'hidden'; 
      } else {
          document.body.style.overflow = 'auto';   
      }
      return () => { document.body.style.overflow = 'auto'; };
  }, [isAnyModalOpen]);

  if (!authUser) return (
      <>
          <style>{`
          * { box-sizing: border-box; } 
          @keyframes zoomIn { 0% {transform: scale(0.9); opacity: 0;} 100% {transform: scale(1); opacity: 1;} } 
          @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
          @keyframes pulseGuide { 0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); } 70% { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); } 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); } }
          .smart-guide-target {
              box-shadow: 0 0 0 4px #10B981, 0 0 20px #10B981, 0 0 0 9999px rgba(0, 0, 0, 0.75) !important;
              outline: 3px solid white !important;
              background-color: #fff !important; 
              color: #000 !important;
              transition: all 0.3s ease !important;
              position: relative !important;
              z-index: 100005 !important;
              pointer-events: auto !important;
          }
              /* 👇 ब्लैक कलर ओवरराइड को बायपास करने के लिए फ़ोर्स क्लासेस (Ultra Strong Selectors) 👇 */
          body .force-grey, span.force-grey, div .force-grey, * .force-grey { color: #64748B !important; }
          body .force-green, span.force-green, div .force-green, * .force-green { color: #10B981 !important; }
      `}</style>

          <LoginView 
             onLogin={(user) => setAuthUser(user)} 
             appBg={appBg} 
             handleVoiceClick={handleVoiceClick}
             handleFocusVoice={handleFocusVoice}
             handleTextBlurVoice={handleTextBlurVoice}
          />
      </>
  );

  if (authUser.role === 'worker') {
      const liveWorkerData = workers.find(w => String(w.id) === String(authUser.id)) || {};
      const updatedWorker = { ...authUser, ...liveWorkerData };
      return <WorkerApp user={updatedWorker} onLogout={handleLogout} workersData={workers} allLooms={mergedLooms} stats={dbStats} />;
  }

  const renderView = () => {
      if (view === 'inventory') return <Inventory 
    onBack={() => setView('dashboard')} 
    appBg={appBg} 
    globalGuide={{
        voiceAssist, setVoiceAssist, showGuideModal, setShowGuideModal,
        activeGuideFlow, currentGuideMessage, toggleVoiceAssist, 
        handleVoiceClick, handleFocusVoice, handleSelectVoice, 
        handleTextBlurVoice, startSmartGuide, endSmartGuide, 
        advanceNextStep, speakHinglish, setPendingConfirm
    }} 
/>;
      if (view === 'profile') return <ProfileSettings 
    onBack={() => setView('dashboard')} 
    onLogout={handleLogout} 
    appBg={appBg} 
    setAppBg={setAppBg} 
    notifOn={notifOn} 
    setNotifOn={setNotifOn}
    globalGuide={{
        voiceAssist, setVoiceAssist, showGuideModal, setShowGuideModal,
        activeGuideFlow, currentGuideMessage, toggleVoiceAssist, 
        handleVoiceClick, handleFocusVoice, handleSelectVoice, 
        handleTextBlurVoice, startSmartGuide, endSmartGuide, 
        advanceNextStep, speakHinglish, setPendingConfirm
    }} 
/>;
      if (view === 'payments') return <PaymentView 
    onBack={() => setView('dashboard')} 
    workers={workers} 
    refreshData={fetchProfileAndData} 
    appBg={appBg} 
    globalGuide={{
        voiceAssist, setVoiceAssist, showGuideModal, setShowGuideModal,
        activeGuideFlow, currentGuideMessage, toggleVoiceAssist, 
        handleVoiceClick, handleFocusVoice, handleSelectVoice, 
        handleTextBlurVoice, startSmartGuide, endSmartGuide, 
        advanceNextStep, speakHinglish, setPendingConfirm
    }} 
/>;
      if (view === 'workers') return <Workers 
    onBack={() => setView('dashboard')} 
    workers={workers} 
    looms={mergedLooms} 
    refreshData={fetchProfileAndData} 
    globalGuide={{
        voiceAssist, setVoiceAssist, showGuideModal, setShowGuideModal,
        activeGuideFlow, currentGuideMessage, toggleVoiceAssist, 
        handleVoiceClick, handleFocusVoice, handleSelectVoice, 
        handleTextBlurVoice, startSmartGuide, endSmartGuide, 
        advanceNextStep, speakHinglish, setPendingConfirm
    }} 
/>;
      if (view === 'badli_history') return <BadliHistory 
    onBack={() => setView('dashboard')} 
    globalGuide={{
        voiceAssist, setVoiceAssist, showGuideModal, setShowGuideModal,
        activeGuideFlow, currentGuideMessage, toggleVoiceAssist, 
        handleVoiceClick, handleFocusVoice, handleSelectVoice, 
        handleTextBlurVoice, startSmartGuide, endSmartGuide, 
        advanceNextStep, speakHinglish, setPendingConfirm
    }} 
/>;
      if (view === 'vacancies') return <Vacancies 
    onBack={() => setView('dashboard')} 
    globalGuide={{
        voiceAssist, setVoiceAssist, showGuideModal, setShowGuideModal,
        activeGuideFlow, currentGuideMessage, toggleVoiceAssist, 
        handleVoiceClick, handleFocusVoice, handleSelectVoice, 
        handleTextBlurVoice, startSmartGuide, endSmartGuide, 
        advanceNextStep, speakHinglish, setPendingConfirm
    }} 
/>;
      return <DashboardView 
        shiftTimings={shiftTimings}
        stats={dbStats} looms={mergedLooms} workers={workers} 
        statsTab={statsTab} setStatsTab={setStatsTab}
        setStatsModalData={setStatsModalData} 
        onOpenProfile={() => setView('profile')} 
        onOpenSearch={() => setShowSearch(true)} 
        onDownloadReport={() => setShowReportModal(true)} 
        loomHistory={loomHistory}
        appBg={appBg} setAppBg={setAppBg} 
        notifOn={notifOn} setNotifOn={setNotifOn}
        voiceAssist={voiceAssist} toggleVoiceAssist={toggleVoiceAssist}
        setShowGuideModal={setShowGuideModal} handleVoiceClick={handleVoiceClick}
        speakHinglish={speakHinglish}
      />;
  };

 // 🚀 NEW: GENERATE DYNAMIC TEXT SIZES FOR ENTIRE OWNER APP
  const textMultiplier = appTextSize / 12; 
  const usedFontSizes = [6, 7, 7.5, 8, 8.5, 9, 9.5, 10, 11, 12, 13, 14, 15, 16, 18, 20, 24, 28, 32, 40, 42];
  const dynamicTextStyles = usedFontSizes.map(size => {
      const perfectFontSize = (size * textMultiplier).toFixed(2);
      return `*[style*="font-size: ${size}px"], *[style*="font-size:${size}px"] { font-size: ${perfectFontSize}px !important; }`;
  }).join('\n');

 return (
    <div style={{...styles.container, background: appBg}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fira+Sans:wght@400;600;800;900&family=Inter:wght@400;600;800;900&family=Montserrat:wght@400;600;800;900&family=Nunito:wght@400;600;800;900&family=Outfit:wght@400;600;800;900&family=Playfair+Display:wght@400;600;800;900&family=Poppins:wght@400;600;800;900&family=Plus+Jakarta+Sans:wght@400;600;800;900&family=Quicksand:wght@400;600;700&family=Raleway:wght@400;600;800;900&display=swap');
        
        /* 👇 NEW: APPLY ZOOM GLOBALLY ON ENTIRE APP 👇 */
        html { 
            zoom: ${appScale} !important; 
            -moz-transform: scale(${appScale}) !important; 
            -moz-transform-origin: top center !important; 
        }

      * { 
          font-family: '${appFont}', sans-serif !important; 
          box-sizing: border-box; 
          -webkit-tap-highlight-color: transparent; 
          text-transform: uppercase !important;
          /* 👇 Click delay eliminate karne aur touch latency hatane ke liye */
          touch-action: manipulation;
          /* 👇 Pure app me hardware rendering pipeline use karne ke liye */
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        /* 👇 iPhone momentum overflow physics globally handle karne ke liye */
        html, body {
          scroll-behavior: smooth !important;
          overscroll-behavior-y: contain;
          -webkit-overflow-scrolling: touch !important;
        }

        /* DYNAMIC SCALING CSS INJECTED HERE */
        ${dynamicTextStyles}

        /* Premium Ultra-Thin Smooth Scrollbar */
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(15, 23, 42, 0.15); border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(15, 23, 42, 0.3); }   

        @keyframes zoomIn { 0% {transform: scale(0.9); opacity: 0;} 100% {transform: scale(1); opacity: 1;} } 
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes pulseGuide { 0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); } 70% { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); } 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); } }
        
        .smart-guide-target {
            box-shadow: 0 0 0 4px #10B981, 0 0 20px #10B981, 0 0 0 9999px rgba(0, 0, 0, 0.75) !important;
            outline: 3px solid white !important;
            background-color: #fff !important; 
            color: #000 !important;
            transition: all 0.3s ease !important;
            position: relative !important;
            z-index: 100005 !important;
            pointer-events: auto !important;
        }
        
        /* Ultra Strong Classes For Colors */
        body .force-grey, span.force-grey, div .force-grey, * .force-grey { color: #64748B !important; }
        body .force-green, span.force-green, div .force-green, * .force-green { color: #10B981 !important; }
      `}</style>

    {/* 👇 यह हमने नई फाइल से इम्पोर्ट किया है और इसके प्रॉप्स पास कर दिए हैं 👇 */}
      <FloatingVoiceAI 
          voiceAssist={voiceAssist} 
          toggleVoiceAssist={toggleVoiceAssist} 
          handleVoiceClick={handleVoiceClick} 
      />
      
      {/* 🚀 PAGE ANIMATION WRAPPER - key={view} ensures animation re-runs on tab switch */}
      <div key={view} className="page-animate" style={{ width: '100%', height: '100%' }}>
          {renderView()}
      </div>
      
  {/* ===== 🌟 PREMIUM SOLID WHITE BOTTOM NAVIGATION FOOTER 🌟 ===== */}
      <BottomNav 
          view={view} 
          setView={setView} 
          setShowGuideModal={setShowGuideModal} 
          handleVoiceClick={handleVoiceClick} 
          activeGuideFlow={activeGuideFlow} 
      />
      
      {/* ===== 🌟 ALL POPUP MODALS (Search, Stats, Report) 🌟 ===== */}
      {showSearch && (
          <SearchModal 
              onClose={() => setShowSearch(false)} 
              workers={workers} 
              looms={mergedLooms} 
              appBg={appBg} 
              handleVoiceClick={handleVoiceClick} 
              handleFocusVoice={handleFocusVoice} 
              handleSelectVoice={handleSelectVoice} 
              handleTextBlurVoice={handleTextBlurVoice} 
          />
      )}

      {statsModalData && (
          <StatsModal 
              title={statsModalData.title} 
              data={statsModalData.data} 
              period={statsModalData.period} 
              onClose={() => setStatsModalData(null)} 
              appBg={appBg} 
              handleVoiceClick={handleVoiceClick}
              shiftTimings={shiftTimings} 
          />
      )}

      {showReportModal && (
          <ProductionReportModal 
              isOpen={showReportModal} 
              onClose={() => setShowReportModal(false)} 
              workers={workers} 
              looms={mergedLooms} 
              stats={dbStats} 
          />
      )}

    </div>
  );
}
      
      

// --- STYLES ---
const styles = {
   container: { 
        zoom: 0.95,
        fontSize: '14px',                       
        minHeight: '100vh', 
        paddingBottom: '100px', 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100%', 
        overflowY: 'auto', 
        zIndex: 100, 
        fontFamily: "'Plus Jakarta Sans', sans-serif", 
        transition: 'background 0.3s ease', 
        /* 👇 App layout scrolls layers physics fixes */
        scrollBehavior: 'smooth',
        WebkitOverflowScrolling: 'touch',
        willChange: 'scroll-position'
    },

    header: { background: 'rgba(255, 255, 255, 0.25)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', padding: '12px 16px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', display: 'flex', alignItems: 'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:10 },
    content: { padding: '16px',paddingBottom: '80px', maxWidth: '600px', margin: '0 auto' }, 
    
    card: { background: 'rgba(255, 255, 255, 0.25)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderRadius: '24px', boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)', border: '1px solid rgba(255, 255, 255, 0.3)', overflow: 'hidden', cursor: 'pointer' }, 
    
    backBtn: { background: 'rgba(255,255,255,0.4)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer', padding: '5px', display:'flex', alignItems:'center' },
    iconBtnSquare: { width:'32px', height:'32px', borderRadius:'10px', border:'1px solid rgba(255,255,255,0.4)', background:'rgba(255,255,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' },
    
    premiumButtonStyle: { 
        width: '100%', background: '#1A1A1A', color: 'white', border: 'none', borderRadius: '16px', padding: '12px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', transition: 'all 0.25s ease'
    },
    
    tabActive: { flex: 1, padding: '10px 8px', background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '16px', fontSize: '11px', fontWeight: '600', color: '#111', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', cursor: 'pointer', transition: 'all 0.2s' },
    tabInactive: { flex: 1, padding: '10px 8px', background: 'transparent', border: 'none', borderRadius: '16px', fontSize: '11px', fontWeight: '500', color: '#444', cursor: 'pointer' },
    
    statsTabActive: { flex: 1, padding: '10px 6px', background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#111', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', cursor: 'pointer', transition: 'all 0.25s ease' },
    statsTabInactive: { flex: 1, padding: '10px 6px', background: 'transparent', border: 'none', borderRadius: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#444', cursor: 'pointer', transition: 'all 0.25s ease' },

    tagActive: { padding: '6px 12px', background: '#1A1A1A', border: 'none', borderRadius: '12px', fontSize: '8px', fontWeight: '600', color: 'white', cursor: 'pointer' },
    tagInactive: { padding: '6px 12px', background: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '12px', fontSize: '8px', fontWeight: '500', color: '#333', cursor: 'pointer' },

   
    pageTitle: { fontSize: '14px', fontWeight: '600', color: '#111', margin: 0, letterSpacing:'-0.5px' },
    
    input: { width: '100%', padding: '12px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.4)', fontSize: '12px', outline: 'none', background: 'rgba(255,255,255,0.5)', color:'#111', fontWeight:'400', boxSizing: 'border-box', transition: 'all 0.2s', ':focus': {background: 'rgba(255,255,255,0.8)'} },
    fieldLabel: { fontSize: '8px', fontWeight: '500', color: '#333', textTransform: 'uppercase', marginBottom: '8px', display: 'block', letterSpacing: '0.8px' },
    fieldValue: { fontSize: '14px', fontWeight: '500', color: '#111', borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: '8px' },
    row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },

    bottomNav: { position: 'fixed', bottom: 0, left: 0, width: '100%', background: 'rgba(255, 255, 255, 0.3)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderTop: '1px solid rgba(255, 255, 255, 0.3)', display: 'flex', justifyContent: 'space-around', padding: '12px 0 16px 0', zIndex: 1000, boxShadow: '0 -4px 30px rgba(0,0,0,0.05)' },
    navItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', fontSize: '8px', color: '#555', gap: '4px', cursor: 'pointer', fontWeight: '400', transition: 'all 0.2s' },
    navItemActive: { display: 'flex', flexDirection: 'column', alignItems: 'center', fontSize: '8px', color: '#111', gap: '4px', cursor: 'pointer', fontWeight: '600', transform: 'translateY(-2px)', transition: 'all 0.2s' },
    sectionHeader: { fontSize: '11px', fontWeight: '600', color: '#333', textTransform: 'uppercase', letterSpacing: '0.8px', marginTop: '24px', marginBottom: '12px', paddingLeft: '8px' }
};

export default App;